import React, { useState, useEffect, useRef } from "react";
import { store } from "../utils/store.js";
import { ai, aiChat } from "../utils/api.js";
import { C, S, CM_COL, GM, RV, NAV, ACTIONS, getCampaignPrompt, NICHES, DEFAULT_NAV, DEFAULT_OUTCOMES, ALL_TEMPLATES, logActivity, SEED_REVIEWS } from "../shared/constants.js";
import { useAIContext } from "../shared/hooks.js";
import { renderMarkdown } from "../shared/components.jsx";
import { CHANGELOG_SUMMARY } from "../shared/changelog.js";

/* ─── KURB AI ASSISTANT — "Command in Chief" (extracted from App.jsx, sub-project 10b) ───
   AI_MODULE_REGISTRY reads cross-module context via window.__aiCtx + window._xxx bridges
   (registered by the App.jsx-resident modules on mount). AIAssistant is a direct (non-lazy)
   import in App.jsx — it is always-present core UI, not a routed page. */

const NAV_LABELS = {
  dashboard:"Dashboard", campaigns:"Campaigns", reviews:"Reviews", crm:"CRM Contacts",
  social:"Social Media", content:"Content Writer", website:"Website Builder", ads:"Ads Manager",
  gbp:"Google Business", marketing:"Guest Messaging", bloggers:"Influencer Finder",
  settings:"Settings", inquiries:"Inquiries", reservations:"Bookings", pricing:"Plans & Pricing",
  roles:"Roles & Permissions", research:"Research & Strategy", integrations:"Integrations",
  reports:"Reports", changelog:"Change Log",
};

const AI_SUGGESTIONS = [
  { icon:"🎯", text:"What should I focus on today?" },
  { icon:"📝", text:"Write a blog post about our best dishes" },
  { icon:"📸", text:"Write an Instagram post for this week's special" },
  { icon:"✉", text:"Draft an email campaign to bring guests back" },
  { icon:"★", text:"How do I respond to my reviews?" },
  { icon:"📈", text:"How can I get more bookings?" },
];

const CW_TYPE_LABEL = { blog:"Blog Post", email:"Email Newsletter", announcement:"Announcement", "menu-desc":"Menu Descriptions", press:"Press Release", about:"About Us" };

function buildAppContext(profile, userMode) {
  const niche = profile?.niche || "restaurant";
  const nicheData = NICHES.find(n => n.id === niche);

  // Navigation — derived from actual NAV / DEFAULT_NAV arrays
  const advancedNavItems = NAV.filter(m => m.id).map(m => m.label).join(", ");
  const defaultNavItems  = DEFAULT_NAV.map(m => m.label).join(", ");

  // Campaign templates — derived from ALL_TEMPLATES
  const templateList = ALL_TEMPLATES.map(t =>
    `"${t.name}" (goal: ${t.goal}, channels: ${t.channels.join(", ")})`
  ).join("; ");

  // Default Mode outcome templates for this niche
  const outcomes = DEFAULT_OUTCOMES[niche] || DEFAULT_OUTCOMES.generic;
  const outcomeList = outcomes.map(o => `"${o.title}": ${o.subtitle}`).join("; ");

  // Available campaign channels
  const channelList = ACTIONS.map(a => `${a.label} (id: ${a.id})`).join(", ");

  // Supported niches
  const nicheList = NICHES.map(n => `${n.label} (id: ${n.id})`).join(", ");

  return `
APP MODES:
- Default Mode: outcome-based, 4 screens (${defaultNavItems}). User picks a goal → answers 2-3 questions → AI generates all channel content and saves campaign. No marketing jargon.
- Advanced Mode: full suite with all modules (${advancedNavItems}). For power users wanting channel-level control.
- Switch modes: Settings → profile dropdown → "Switch to Advanced/Default Mode". Both modes share the same data.
- Current user mode: ${userMode === "default" ? "Default Mode" : "Advanced Mode"}

BUSINESS NICHES SUPPORTED: ${nicheList}
Current business niche: ${nicheData?.label || niche} — "${profile?.name || "this business"}"

DEFAULT MODE — OUTCOME TEMPLATES FOR THIS NICHE (${nicheData?.label || niche}):
${outcomeList}

CAMPAIGN SYSTEM:
- How it works: user picks template or outcome → AI generates content for every enabled channel simultaneously → campaign saved as active with a completed run ready to review
- Available channels: ${channelList}
- Advanced Mode templates: ${templateList}
- Campaigns can also be created via AI chat: say "create a [template name] campaign" → AI uses create_campaign action to build and save it immediately
- Content is generated and saved; actual publishing to live platforms requires API integrations (in progress)

ADVANCED MODE MODULES (auto-derived from navigation):
${NAV.filter(m => m.id).map(m => `- ${m.label} (id: ${m.id})`).join("\n")}

RECENT FEATURE HISTORY (from changelog):`;
}

// ── AI Module Registry — add new modules here, everything else is automatic ──
const AI_MODULE_REGISTRY = {
  website: {
    contextFn: (profile) => {
      const wbCtx = typeof window._wbGetContext === "function" ? window._wbGetContext() : null;
      if (!wbCtx) return null;
      return `WEBSITE EDITOR MODE\nYou are directly editing ${wbCtx.bizName || profile?.name || "this business"}'s live website.\nActive page: "${wbCtx.pageName || "Home"}" | All pages: ${(wbCtx.pages || []).join(", ") || "Home"}\n\nWhen the user asks to change anything on the website — text, colors, fonts, sections, layout, buttons, images, spacing — respond with a single short sentence confirming what you're doing, e.g. "Updated the hero heading to Signature Dishes." The change is applied to the live preview automatically. Be specific about what you changed. Never use future tense — speak as if it's already done.`;
    },
    intents: `website_edit|<brief description of what to change>`,
    rules: `- website_edit → user is asking to change anything on the website (text, colors, fonts, sections, layout, buttons, images, style, design — ANY visual or content edit)`,
    screenPref: `- On the website screen, prefer website_edit over general for any edit-sounding request`,
    validTypes: ["website_edit"],
    parseParams: { website_edit: (p) => ({ brief: p[1]||"" }) },
  },

  reviews: {
    contextFn: (profile) => {
      const rv = (typeof window !== "undefined" && window.__aiCtx?.["reviews"]) ? window.__aiCtx["reviews"]() : null;
      if (!rv) return null;
      const unres = (rv.unresponded || []).slice(0, 5);
      return `REVIEWS MODULE — Live data:\n${rv.total} total reviews, avg ${rv.avgRating}★, ${rv.unresponded?.length || 0} need a response\n\n${unres.length ? `Unresponded reviews:\n${unres.map(r => `• ${r.reviewer||r.reviewerName||"Guest"} (${r.rating}★, ${r.date||""}): "${(r.text||"").slice(0,100)}${(r.text||"").length>100?"…":""}"`).join("\n")}` : "✓ All reviews responded to."}\n\nYou can: respond to specific reviews, respond to all unresponded, summarize review sentiment, generate a review request message, or give advice on improving the rating.\nWhen the user asks to respond to a review, confirm briefly — the reply cards appear automatically.`;
    },
    intents: `respond_review|<reviewer name, or 'all', or 'negative', or 'latest'>\nsummarize_reviews\nrequest_review|<channel: email|sms|message>`,
    rules: `- respond_review → user wants to reply to a review (respond, reply, answer, address, write a response)\n- summarize_reviews → user wants insights, sentiment analysis, or a summary of their reviews\n- request_review → user wants to ask customers for reviews (request, ask, send, generate request message)`,
    screenPref: `- On the reviews screen, prefer respond_review/summarize_reviews/request_review over general for any review-related action`,
    validTypes: ["respond_review", "summarize_reviews", "request_review"],
    parseParams: {
      respond_review:    (p) => ({ target: p[1]||"latest" }),
      summarize_reviews: (p) => ({}),
      request_review:    (p) => ({ channel: p[1]||"message" }),
    },
  },

  crm: {
    contextFn: (profile) => {
      const c = (typeof window !== "undefined" && window.__aiCtx?.["crm"]) ? window.__aiCtx["crm"]() : null;
      if (!c) return null;
      return `CRM MODULE — Live data:\n${c.total} contacts total\nRecent: ${(c.recent||[]).map(x=>`${x.name} (${x.type}, ${x.email||x.phone||"no contact info"})`).join(", ")||"none"}\n\nYou can: add new contacts, find contacts, add notes, look up contact info, help with outreach messages.`;
    },
    intents: `add_contact|<name>|<email or phone>\nfind_contact|<name or email>\nadd_note|<contact name>|<note text>`,
    rules: `- add_contact → user wants to add/create/save a new contact\n- find_contact → user wants to look up, find, or search for a contact\n- add_note → user wants to add a note or update info for an existing contact`,
    screenPref: `- On the CRM screen, prefer add_contact/find_contact/add_note over general for any contact-related action`,
    validTypes: ["add_contact", "find_contact", "add_note"],
    parseParams: {
      add_contact:  (p) => ({ name: p[1]||"", contact: p[2]||"" }),
      find_contact: (p) => ({ query: p[1]||"" }),
      add_note:     (p) => ({ name: p[1]||"", note: p[2]||"" }),
    },
  },

  campaigns: {
    contextFn: (profile) => {
      const c = (typeof window !== "undefined" && window.__aiCtx?.["campaigns"]) ? window.__aiCtx["campaigns"]() : null;
      if (!c) return null;
      return `CAMPAIGNS MODULE — Live data:\n${c.active||0} active, ${c.draft||0} draft, ${c.paused||0} paused, ${c.total||0} total campaigns\nRecent: ${(c.recent||[]).map(x=>`${x.name} (${x.status||"draft"})`).join(", ")||"none"}\n\nYou can: check campaign status, pause or resume campaigns, create new campaigns, explain campaign performance.`;
    },
    intents: `campaign_status\npause_campaign|<campaign name>\nresume_campaign|<campaign name>`,
    rules: `- campaign_status → user asks about campaigns, what's running, or performance overview\n- pause_campaign → user wants to pause or stop a campaign\n- resume_campaign → user wants to resume or restart a paused campaign`,
    screenPref: `- On the campaigns screen, prefer campaign_status/pause_campaign/resume_campaign over general for campaign-related requests`,
    validTypes: ["campaign_status", "pause_campaign", "resume_campaign"],
    parseParams: {
      campaign_status: (p) => ({}),
      pause_campaign:  (p) => ({ name: p[1]||"" }),
      resume_campaign: (p) => ({ name: p[1]||"" }),
    },
  },

  social: {
    contextFn: (profile) => {
      return `SOCIAL MEDIA MODULE\nYou can write posts for Instagram, Facebook, TikTok, Twitter/X, or LinkedIn. When the user asks to create/write/draft a post, the post card will appear automatically.\nFor platform-specific requests default to that platform. If no platform mentioned, default to Instagram.`;
    },
    intents: ``,
    rules: ``,
    screenPref: `- On the social screen, strongly prefer write_social over general for any post/content creation request`,
    validTypes: [],
    parseParams: {},
  },

  dashboard: {
    contextFn: (profile) => {
      return `DASHBOARD — You have full visibility of this business. Proactively surface what needs attention. Be action-oriented and specific.`;
    },
    intents: ``,
    rules: ``,
    screenPref: ``,
    validTypes: [],
    parseParams: {},
  },

  reservations: {
    contextFn: (profile) => {
      const r = typeof window._reservationGetContext === "function" ? window._reservationGetContext() : null;
      if (!r) return null;
      return `BOOKINGS MODULE — Live data:\n${r.total} total bookings, ${r.todayCount||0} confirmed for today\nUpcoming: ${(r.upcoming||[]).map(x=>`${x.name} — ${x.date} ${x.time}, party of ${x.party}`).join(" | ")||"none"}\n\nYou can: add a new booking, show upcoming reservations, cancel a booking, check availability.\nWhen adding a booking, confirm what you're creating — the booking is saved automatically.`;
    },
    intents: `add_reservation|<name>|<date YYYY-MM-DD>|<time HH:MM>|<party size>\nreservation_status\ncancel_reservation|<name or date>`,
    rules: `- add_reservation → user wants to book/add/create a reservation or appointment\n- reservation_status → user asks about upcoming bookings or today's schedule\n- cancel_reservation → user wants to cancel or remove a booking`,
    screenPref: `- On the bookings screen, prefer add_reservation/reservation_status/cancel_reservation over general for any booking-related request`,
    validTypes: ["add_reservation", "reservation_status", "cancel_reservation"],
    parseParams: {
      add_reservation:    (p) => ({ name: p[1]||"", date: p[2]||"", time: p[3]||"", party: p[4]||"2" }),
      reservation_status: (p) => ({}),
      cancel_reservation: (p) => ({ target: p[1]||"" }),
    },
  },

  gbp: {
    contextFn: (profile) => {
      return `GOOGLE BUSINESS PROFILE MODULE\nYou can: create GBP posts (optimized for Google's format), draft Q&A answers, generate update announcements, advise on GBP optimization.\nGBP posts: max 1,500 characters, no hashtags, end with a clear CTA (call, visit, book).\nWhen user asks to create/write/draft a GBP post, confirm briefly — the post card appears automatically.`;
    },
    intents: `create_gbp_post|<topic or goal>\ngbp_qa|<topic>`,
    rules: `- create_gbp_post → user wants to write/create/draft a Google Business Profile post\n- gbp_qa → user wants to draft Q&A answers for their GBP listing`,
    screenPref: `- On the GBP screen, prefer create_gbp_post/gbp_qa over general for any content creation request`,
    validTypes: ["create_gbp_post", "gbp_qa"],
    parseParams: {
      create_gbp_post: (p) => ({ topic: p[1]||"" }),
      gbp_qa:          (p) => ({ topic: p[1]||"general" }),
    },
  },

  aeo: {
    contextFn: (profile) => {
      const ctx = typeof window._aeoGetContext === "function" ? window._aeoGetContext() : null;
      if (!ctx) return null;
      return `AI VISIBILITY context: Overall score ${ctx.score}/100. Tracking ${ctx.totalQueries} queries across ChatGPT, Perplexity, Claude, Google AI Overview and Google AI Mode.`;
    },
    intents:    `aeo_suggest_queries\naeo_run_check|<query>`,
    rules:      `- aeo_suggest_queries → user wants to generate or suggest AI visibility queries\n- aeo_run_check → user wants to check a specific query`,
    screenPref: `- On AI Visibility screen, prefer aeo actions over general`,
    validTypes: ["aeo_suggest_queries","aeo_run_check"],
    parseParams: {
      aeo_suggest_queries: () => ({}),
      aeo_run_check:       (p) => ({ query: p[1]||"" }),
    },
  },

  seo: {
    contextFn: (profile) => {
      const ctx = typeof window._seoGetContext === "function" ? window._seoGetContext() : null;
      if (!ctx) return null;
      const lines = [`SEO MODULE — Active tab: ${ctx.tab}`];
      if (ctx.auditScore != null) lines.push(`Site health score: ${ctx.auditScore}/100${ctx.auditIssues?.length ? ` · Issues: ${ctx.auditIssues.join(", ")}` : " · No critical issues"}`);
      if (ctx.rankings?.length) {
        const ranked = ctx.rankings.filter(r => r.position);
        lines.push(`Rankings checked: ${ctx.rankings.length} keywords, ${ranked.length} ranking · Top: ${ranked.slice(0,4).map(r=>`"${r.keyword}" #${r.position}`).join(", ")||"none in top 10"}`);
      }
      if (ctx.trackedKws?.length) lines.push(`Tracked keywords (${ctx.trackedKws.length}): ${ctx.trackedKws.map(k=>`"${k.keyword}"${k.position?` #${k.position}`:""}`).join(", ")}`);
      if (ctx.topOpportunities?.length) lines.push(`Top keyword opportunities: ${ctx.topOpportunities.slice(0,5).map(o=>`"${o.keyword}" (${o.intent}, ${o.difficulty})`).join(", ")}`);
      if (ctx.hasPlan) lines.push(`Content plan has been generated.`);
      lines.push(`\nYou can advise on SEO strategy, explain what the data means, suggest which keywords to target, help prioritize content, and explain how to improve rankings.`);
      return lines.join("\n");
    },
    intents:    ``,
    rules:      ``,
    screenPref: `- On the SEO screen, give specific data-driven advice using the ranking and opportunity data above`,
    validTypes: [],
    parseParams: {},
  },

  content: {
    contextFn: (profile) => {
      const ctx = (typeof window !== "undefined" && window.__aiCtx?.["content"]) ? window.__aiCtx["content"]() : null;
      if (!ctx) return null;
      const lines = [`CONTENT MODULE — Tab: ${ctx.cwTab}`];
      if (ctx.cwTab === "write") {
        if (ctx.topic) lines.push(`Working on: ${ctx.contentType} — "${ctx.topic.slice(0,80)}${ctx.topic.length>80?"…":""}"`);
        if (ctx.keyword) lines.push(`Target SEO keyword: "${ctx.keyword}"`);
        if (ctx.hasGeneratedContent) lines.push(`Content has been generated — user may want to refine, save, or publish it.`);
        else lines.push(`Content not yet generated — user may want to write or adjust their topic.`);
      }
      lines.push(`Library: ${ctx.libraryCount} saved pieces · Topics: ${ctx.topicCount} ideas`);
      lines.push(`\nYou can: help write or improve content, suggest better topics, advise on SEO keywords, explain content strategy, help the user decide what to write next.`);
      return lines.join("\n");
    },
    intents:    ``,
    rules:      ``,
    screenPref: `- On the content screen, help the user write, improve, or plan their content`,
    validTypes: [],
    parseParams: {},
  },

  research: {
    contextFn: (profile) => {
      const ctx = (typeof window !== "undefined" && window.__aiCtx?.["research"]) ? window.__aiCtx["research"]() : null;
      if (!ctx) return null;
      const lines = [`RESEARCH & STRATEGY MODULE — Tab: ${ctx.tab}`];
      if (ctx.topKeywords?.length) lines.push(`Keywords researched: ${ctx.topKeywords.map(k=>`"${k.keyword}" (${k.volume?.toLocaleString()||"?"}mo)`).join(", ")}`);
      if (ctx.topIdeas?.length) lines.push(`Content ideas generated: ${ctx.topIdeas.map(i=>`"${i.title}" (${i.type})`).join(", ")}`);
      if (ctx.competitors?.length) lines.push(`Competitors tracked: ${ctx.competitors.join(", ")}`);
      lines.push(`Saved: ${ctx.savedKwLists} keyword lists, ${ctx.savedIdeaLists} idea lists`);
      lines.push(`\nYou can: suggest keywords to research, help plan a content strategy, explain competitor insights, recommend what to prioritize.`);
      return lines.join("\n");
    },
    intents:    ``,
    rules:      ``,
    screenPref: `- On research screen, help user discover keywords and plan their content strategy`,
    validTypes: [],
    parseParams: {},
  },

  gm: {
    contextFn: (profile) => {
      const ctx = (typeof window !== "undefined" && window.__aiCtx?.["gm"]) ? window.__aiCtx["gm"]() : null;
      if (!ctx) return null;
      return `GUEST MARKETING MODULE — Tab: ${ctx.tab}\nAudience: ${ctx.totalContacts} contacts total, ${ctx.vipCount} VIP guests.\nYou can: help write email or SMS campaign copy, suggest subject lines, advise on campaign timing and segmentation, help re-engage lapsed guests.`;
    },
    intents:    ``,
    rules:      ``,
    screenPref: `- On guest marketing screen, help write compelling email/SMS campaigns for the guest list`,
    validTypes: [],
    parseParams: {},
  },

  conversion: {
    contextFn: (profile) => {
      const d = window.__aiCtx?.["conversion"];
      if (!d) return null;
      return `CONVERSION module context: ${d.hot_leads} hot leads need attention. Active pipeline: New=${d.pipeline_by_status?.new||0}, Contacted=${d.pipeline_by_status?.contacted||0}, Qualified=${d.pipeline_by_status?.qualified||0}, Booked=${d.pipeline_by_status?.booked||0}. Won this month: $${d.won_revenue_month||0}. Top converting channel: ${d.top_source||"unknown"}.`;
    },
    intents:    "show_conversion_pipeline\nconversion_overview\nadd_conversion_lead",
    rules:      "- show_conversion_pipeline → user asks to see leads, pipeline, conversion status\n- conversion_overview → user asks about revenue, won deals, lead stats\n- add_conversion_lead → user wants to add a lead manually",
    screenPref: "- On this screen, prefer conversion_overview for revenue or lead questions",
    validTypes: ["show_conversion_pipeline","conversion_overview","add_conversion_lead"],
    parseParams: {
      show_conversion_pipeline: (p) => ({ status: p[1] || "all" }),
      conversion_overview:      (p) => ({}),
      add_conversion_lead:      (p) => ({ name: p[1] || "" }),
    },
  },

  brain: {
    contextFn: async (profile) => {
      // Read brain from sessionStorage cache (set by useAIContext)
      try {
        const raw = sessionStorage.getItem("__aiCtx_business-brain");
        if (!raw) return null;
        const d = JSON.parse(raw);
        const lines = ["BUSINESS BRAIN — What we know about this business:"];
        if (d.totalMenuItems > 0)   lines.push(`Menu: ${d.totalMenuItems} items across ${d.menuSections} sections. Highlights: ${(d.topDishes||[]).join(", ")}`);
        if (d.teamCount > 0)        lines.push(`Team: ${(d.team||[]).map(m=>`${m.name} (${m.role})`).join(", ")}`);
        if (d.activeEvents?.length) lines.push(`Active events/specials: ${d.activeEvents.map(e=>`${e.name}${e.schedule?` — ${e.schedule}`:""}`).join("; ")}`);
        if (d.usp)                  lines.push(`What makes them unique: ${d.usp}`);
        if (d.specialties)          lines.push(`Specialties: ${d.specialties}`);
        if (d.story)                lines.push(`Story: ${d.story.slice(0,200)}`);
        if (d.brandVoice)           lines.push(`Brand voice: ${d.brandVoice}`);
        if (d.topReviews?.length)   lines.push(`Customer says: "${d.topReviews[0]}"`);
        if (d.taggedPhotos > 0)     lines.push(`Tagged photos in library: ${d.taggedPhotos}`);
        if (d.proofCount > 0)       lines.push(`Awards/certifications: ${d.proofCount} items`);
        lines.push(`\nWhen writing content, use specific menu items, team names, events, and USP from above — never generic phrases.`);
        return lines.join("\n");
      } catch { return null; }
    },
    intents:    ``,
    rules:      ``,
    screenPref: `- On the Business Brain screen, help the user understand what data is in their brain and what content it enables`,
    validTypes: [],
    parseParams: {},
  },
};

function ReviewReplyCard({ review, biz, cat, loc, onNavigate, onClose }) {
  const name = review.reviewer || review.reviewerName || "Guest";
  const [reply, setReply] = useState(review.generatedReply || "");
  const [posted, setPosted] = useState(false);
  const [regen, setRegen] = useState(false);

  const postReply = async () => {
    const all = await store.get("reviews") || [];
    const updated = all.map(r => r.id === review.id ? { ...r, responded: true, response: reply } : r);
    await store.set("reviews", updated);
    window.dispatchEvent(new CustomEvent("reviews-updated"));
    setPosted(true);
  };

  const regenerate = async () => {
    setRegen(true);
    try {
      const fresh = await ai(
        `Write a different professional, warm response to this ${review.rating}-star Google review for ${biz} (${cat}${loc}).\n\nReviewer: ${name}\nReview: "${review.text || "No text provided"}"\n\nMake it sound different from the previous reply. Keep under 120 words. Sound like a real business owner. Do NOT start with "Thank you for your review".\n\nReturn ONLY the reply text.`,
        `You are ${biz}'s owner writing heartfelt Google review responses.`,
        false, 350
      );
      setReply(fresh.trim());
    } catch(e) { console.warn("regen failed", e); }
    setRegen(false);
  };

  return (
    <div style={{ background:C.s1, border:`1px solid ${posted ? C.success+"60" : C.border}`, borderRadius:10, overflow:"hidden" }}>
      {/* Review header */}
      <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"flex-start", gap:10 }}>
        <div style={{ width:32, height:32, borderRadius:"50%", background:`${RV}20`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:RV, flexShrink:0 }}>
          {name.charAt(0).toUpperCase()}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:13, fontWeight:700, color:C.text }}>{name}</span>
            <span style={{ fontSize:12, color:RV }}>{"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}</span>
            {review.date && <span style={{ fontSize:11, color:C.t3 }}>{review.date}</span>}
          </div>
          <div style={{ fontSize:12, color:C.t2, marginTop:3, lineHeight:1.5 }}>{(review.text||"").slice(0, 140)}{(review.text||"").length > 140 ? "…" : ""}</div>
        </div>
      </div>
      {/* AI-generated reply — editable */}
      <div style={{ padding:"10px 14px" }}>
        <div style={{ fontSize:10, color:C.t3, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>
          {posted ? "✓ Reply Posted" : "AI-Generated Reply — edit before posting"}
        </div>
        <textarea
          value={reply} onChange={e => setReply(e.target.value)} disabled={posted || regen}
          style={{ ...S.input, resize:"vertical", minHeight:80, fontSize:12, lineHeight:1.6,
            background: posted ? `${C.success}08` : C.s2,
            color: posted ? C.t2 : C.text, border:`1px solid ${posted ? C.success+"40" : C.border}` }}
        />
      </div>
      {!posted && (
        <div style={{ padding:"0 14px 12px", display:"flex", gap:8 }}>
          <button onClick={regenerate} disabled={regen}
            style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px", flex:1 }}>
            {regen ? "Regenerating…" : "↺ Regenerate"}
          </button>
          <button onClick={postReply} disabled={!reply.trim() || regen}
            style={{ ...S.btn, background:RV, fontSize:11, padding:"5px 14px", flex:2 }}>
            ✓ Post Reply
          </button>
        </div>
      )}
      {posted && (
        <div style={{ padding:"0 14px 12px" }}>
          <button onClick={() => { onClose?.(); onNavigate?.("reviews"); }}
            style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px", width:"100%" }}>
            View in Reviews →
          </button>
        </div>
      )}
    </div>
  );
}

function AIAssistant({ profile, mod, userMode = "default", onNavigate, onClose, wlConfig, workspaceId }) {
  // Convenience: business context used across classifyIntent, executeAction, and JSX card rendering
  const biz = profile?.name || "this business";
  const cat = profile?.category || "local business";
  const loc = profile?.city ? ` in ${profile.city}` : "";
  const wsKey = workspaceId || "default"; // namespace all session keys by workspace

  const [messages, setMessages]   = useState(() => {
    try {
      const saved = sessionStorage.getItem(`ai-chat-messages-${wsKey}`);
      if (saved) return JSON.parse(saved).map(m => ({ ...m, actionLoading: false }));
    } catch {}
    return [];
  });
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [ctx, setCtx]             = useState(null);
  const [history, setHistory]     = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [viewingConv, setViewingConv] = useState(null);
  const [kwSave, setKwSave]           = useState({});
  const [topicSave, setTopicSave]     = useState({}); // { [msgId]: { savedItems, openMenu, newListName, savedAll, savedName, error } }
  const [savedTopicLists, setSavedTopicLists] = useState([]); // mirror of topic-lists store for dropdown
  const [panelWidth, setPanelWidth]   = useState(() => { try { return parseInt(localStorage.getItem("ai-panel-width")) || 440; } catch { return 440; } });
  const [briefingShown, setBriefingShown] = useState(() => {
    try { return sessionStorage.getItem(`ai-briefing-shown-${wsKey}`) === "1"; } catch { return false; }
  });
  const bottomRef                     = useRef(null);
  const inputRef                      = useRef(null);
  const abortCampaignRef              = useRef(false);
  const dragRef                       = useRef(null); // { startX, startWidth }

  const onDragStart = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: panelWidth };
    const onMove = (e) => {
      if (!dragRef.current) return;
      const delta = dragRef.current.startX - e.clientX; // dragging left = wider
      const next  = Math.min(600, Math.max(280, dragRef.current.startWidth + delta));
      setPanelWidth(next);
      try { localStorage.setItem("ai-panel-width", next); } catch {}
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor    = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const lastKeywordsRef               = useRef(null);
  const lastTopicsRef                 = useRef(null);

  const fmtRelDate = (iso) => {
    const d = new Date(iso); const now = new Date();
    const days = Math.floor((now - d) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7)  return `${days} days ago`;
    if (days < 14) return "Last week";
    return d.toLocaleDateString("en-US", { month:"short", day:"numeric" });
  };

  const archiveChat = async (msgs) => {
    if (!msgs || msgs.length < 2) return;
    const firstUser = msgs.find(m => m.role === "user");
    const conv = { id: Date.now(), date: new Date().toISOString(), title: firstUser?.content?.slice(0,60) || "Chat", messages: msgs };
    const existing = await store.get("ai-chat-history") || [];
    await store.set("ai-chat-history", [conv, ...existing].slice(0, 50));
    setHistory(prev => [conv, ...prev].slice(0, 50));
  };

  // Keep topic-lists mirror in sync
  useEffect(() => {
    store.get("topic-lists").then(d => setSavedTopicLists(d || []));
    const sync = () => store.get("topic-lists").then(d => setSavedTopicLists(d || []));
    window.addEventListener("topic-list-saved", sync);
    return () => window.removeEventListener("topic-list-saved", sync);
  }, []);

  // Load history & auto-archive if messages are from a previous day
  useEffect(() => {
    store.get("ai-chat-history").then(d => { if (d) setHistory(d); });
    const savedDate = sessionStorage.getItem(`ai-chat-date-${wsKey}`);
    const today = new Date().toDateString();
    if (savedDate && savedDate !== today) {
      const saved = sessionStorage.getItem(`ai-chat-messages-${wsKey}`);
      if (saved) {
        try { archiveChat(JSON.parse(saved)); } catch {}
      }
      sessionStorage.removeItem(`ai-chat-messages-${wsKey}`);
      setMessages([]);
    }
    sessionStorage.setItem(`ai-chat-date-${wsKey}`, today);
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(`ai-chat-messages-${wsKey}`, JSON.stringify(messages)); } catch {}
  }, [messages]);

  const startNewChat = async () => {
    await archiveChat(messages);
    setMessages([]);
    sessionStorage.removeItem(`ai-chat-messages-${wsKey}`);
    setShowHistory(false);
    setViewingConv(null);
  };

  // Load live context data once
  useEffect(() => {
    (async () => {
      const [reviews, campaigns, contacts, reservations] = await Promise.all([
        store.get("reviews"), store.get("gm-campaigns"),
        store.get("crm-contacts"), store.get("reservations"),
      ]);
      const revList  = reviews    || [];
      const campList = campaigns  || [];
      const resList  = reservations || [];
      const newCtx = {
        reviewCount:       revList.length,
        avgRating:         revList.length ? (revList.reduce((a,r)=>a+r.rating,0)/revList.length).toFixed(1) : null,
        unresponded:       revList.filter(r=>!r.responded).length,
        activeCampaigns:   campList.filter(c=>c.status==="active").length,
        totalCampaigns:    campList.length,
        contacts:          (contacts||[]).length,
        upcomingBookings:  resList.filter(r=>r.status==="confirmed").length,
      };
      setCtx(newCtx);

      // Dashboard morning briefing — show once per session on first load
      if (mod === "dashboard" && !briefingShown && messages.length === 0) {
        setBriefingShown(true);
        try { sessionStorage.setItem(`ai-briefing-shown-${wsKey}`, "1"); } catch {}
        try {
          const brief = await ai(
            `You are the AI assistant for ${biz}. Generate a SHORT morning briefing (3-4 bullet points max) based on this data:
- Unresponded reviews: ${newCtx.unresponded}
- Active campaigns: ${newCtx.activeCampaigns}
- Total contacts: ${newCtx.contacts}
- Upcoming bookings: ${newCtx.upcomingBookings}
- Avg rating: ${newCtx.avgRating ?? "?"}★

Highlight what needs attention today. Be specific and action-oriented. Use plain text with • bullets. No greeting. Under 80 words.`,
            "You are a business AI assistant giving a morning briefing. Be concise and actionable.",
            false, 200
          );
          setMessages([{ role:"assistant", content: brief, isDashboardBrief: true }]);
        } catch {}
      }
    })();
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages, loading]);

  // Conversational system — no action tags, just context + navigation
  const buildChatSystem = () => {
    // Current module: full context from registry
    const currentCtx = (() => {
      const entry = AI_MODULE_REGISTRY[mod];
      return entry?.contextFn ? (entry.contextFn(profile) || "") : "";
    })();

    // All OTHER modules: compact context from live window.__aiCtx + sessionStorage cache
    const otherContextLines = [];
    const seen = new Set([mod]);
    // Live contexts (modules currently mounted)
    for (const [key, fn] of Object.entries(window.__aiCtx || {})) {
      if (seen.has(key)) continue;
      seen.add(key);
      try {
        const val = fn();
        if (val) otherContextLines.push(typeof val === "string" ? val.split("\n")[0] : `[${key}] ${JSON.stringify(val).slice(0,120)}`);
      } catch {}
    }
    // Cached contexts (modules visited this session but now unmounted)
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (!k?.startsWith("__aiCtx_")) continue;
      const key = k.replace("__aiCtx_", "");
      if (seen.has(key)) continue;
      seen.add(key);
      try {
        const raw = sessionStorage.getItem(k);
        const val = raw?.startsWith("{") ? JSON.parse(raw) : raw;
        if (val) otherContextLines.push(typeof val === "string" ? `[${key}] ${val.split("\n")[0]}` : `[${key}] ${JSON.stringify(val).slice(0,120)}`);
      } catch {}
    }
    const otherCtx = otherContextLines.length ? `\nOther modules (last known state):\n${otherContextLines.join("\n")}` : "";

    // Inject Business Brain context prominently — it's the most important business-specific data
    const brainCtx = (() => {
      try {
        const raw = sessionStorage.getItem("__aiCtx_business-brain");
        if (!raw) return "";
        const d = JSON.parse(raw);
        const lines = [];
        if (d.totalMenuItems > 0)   lines.push(`Menu: ${d.totalMenuItems} items. Top dishes: ${(d.topDishes||[]).slice(0,6).join(", ")}`);
        if (d.teamCount > 0)        lines.push(`Team: ${(d.team||[]).map(m=>`${m.name} (${m.role})`).join(", ")}`);
        if (d.activeEvents?.length) lines.push(`Events/Specials: ${d.activeEvents.map(e=>`${e.name}${e.schedule?` (${e.schedule})`:""}`).join("; ")}`);
        if (d.usp)                  lines.push(`USP: ${d.usp}`);
        if (d.specialties)          lines.push(`Specialties: ${d.specialties}`);
        if (d.story)                lines.push(`Story: ${d.story.slice(0,150)}`);
        if (d.brandVoice)           lines.push(`Brand voice: ${d.brandVoice}`);
        if (d.topReviews?.length)   lines.push(`Best review: "${d.topReviews[0].slice(0,120)}"`);
        return lines.length ? `\nBusiness Brain (use this for specific, grounded content):\n${lines.join("\n")}` : "";
      } catch { return ""; }
    })();

    return `You are Kurb AI — the Command in Chief for ${profile?.name || "this business"}, a ${profile?.category || "local business"}${profile?.city ? ` in ${profile.city}` : ""}. You know this business inside out. You have access to their menu, team, events, reviews, and brand — use this to give specific, personal, actionable responses — never generic advice.

Live business data:
- Screen open: ${NAV_LABELS[mod] || mod}
- Reviews: ${ctx?.reviewCount ?? "?"} total, avg ${ctx?.avgRating ?? "?"}★, ${ctx?.unresponded ?? "?"} unresponded
- Campaigns: ${ctx?.activeCampaigns ?? "?"} active / ${ctx?.totalCampaigns ?? "?"} total
- CRM contacts: ${ctx?.contacts ?? "?"} | Upcoming bookings: ${ctx?.upcomingBookings ?? "?"}
${brainCtx}
${buildAppContext(profile, userMode)}
${CHANGELOG_SUMMARY}

To send the user to a module, place this at the END of your reply (never mid-sentence):
[NAVIGATE:module_id]
Available module IDs: dashboard, campaigns, research, reviews, crm, social, content, integrations, website, ads, gbp, marketing, bloggers, inquiries, reservations, reports, roles

${currentCtx}${otherCtx}

Rules:
- 1-3 sentences max. Plain conversational text only — no markdown, no bullet points, no numbered lists, no bold.
- The system automatically handles creating content, campaigns, keywords, topics, and review replies — you just need to confirm in one sentence what you're doing.
- Answer questions directly and helpfully. Be specific to this business and location.`;
  };

  // Intent classifier — runs in parallel with chat, determines what action to take
  const classifyIntent = async (userText) => {
    const recent = messages.slice(-4).map(m => `${m.role}: ${(m.content||"").slice(0,80)}`).join("\n");
    const validModules = "dashboard,campaigns,research,reviews,crm,social,content,integrations,website,ads,gbp,marketing,bloggers,inquiries,reservations,reports,roles";
    const tplIds = [...ALL_TEMPLATES.map(t=>t.id), ...Object.values(DEFAULT_OUTCOMES).flat().map(o=>o.id)].join(",");
    try {
      const raw = await ai(
        `Business: ${biz}, ${cat}${loc}
Current screen: ${NAV_LABELS[mod] || mod}
Recent chat:
${recent}

User message: "${userText}"

Classify intent. Reply with ONLY one line:

${(() => { const e = AI_MODULE_REGISTRY[mod]; return e?.intents ? e.intents + "\n" : ""; })()}suggest_topics|<focus phrase>
suggest_keywords|<focus phrase>
create_campaign|<template_id>|<brief>
write_content|<type: blog|email|press|announcement|about>|<title>
write_social|<platform: instagram|facebook|tiktok|twitter|linkedin>|<topic>
save_topics|<list name>
save_keywords|<list name>
draft_campaign|<type: email|sms>|<goal>
navigate|<module_id>
general

Template IDs: ${tplIds}
Module IDs: ${validModules}

Key rules:
${(() => { const e = AI_MODULE_REGISTRY[mod]; return e?.rules ? e.rules + "\n" : ""; })()}- suggest_topics → ANY request for topic ideas, blog ideas, content ideas, "more topics", "more ideas", "what to write", content calendar
- suggest_keywords → keywords, SEO terms, search terms, what to rank for
- save_topics / save_keywords → user wants to save the list just generated
- navigate → user wants to go to a specific section of the app
- general → questions, advice, greetings, anything else${(() => { const e = AI_MODULE_REGISTRY[mod]; return e?.screenPref ? "\n" + e.screenPref : ""; })()}

Reply with ONLY the single classification line, nothing else.`,
        "You are an intent classifier. Output exactly one classification line, nothing else.",
        false, 80
      );
      const line = raw.trim().split("\n")[0].trim();
      const parts = line.split("|");
      const type = parts[0].toLowerCase().trim();
      const BASE_VALID = ["suggest_topics","suggest_keywords","create_campaign","write_content","write_social","save_topics","save_keywords","draft_campaign","navigate","general"];
      const REGISTRY_TYPES = Object.values(AI_MODULE_REGISTRY).flatMap(e => e.validTypes || []);
      const VALID = [...new Set([...BASE_VALID, ...REGISTRY_TYPES])];
      if (!VALID.includes(type)) return { type:"general", params:{} };
      const registryParams = (() => {
        for (const entry of Object.values(AI_MODULE_REGISTRY)) {
          if (entry.parseParams?.[type]) return entry.parseParams[type](parts);
        }
        return null;
      })();
      const paramMap = {
        suggest_topics:   { focus: parts[1]||"" },
        suggest_keywords: { focus: parts[1]||"" },
        create_campaign:  { template: parts[1]||"grand_opening", topic: parts[2]||"" },
        write_content:    { type: parts[1]||"blog", title: parts[2]||"" },
        write_social:     { platform: parts[1]||"instagram", topic: parts[2]||"" },
        save_topics:      { name: parts[1]||"My Topics" },
        save_keywords:    { name: parts[1]||"My Keywords" },
        draft_campaign:   { type: parts[1]||"email", goal: parts[2]||"" },
        navigate:         { module: parts[1]||"" },
        general:          {},
      };
      return { type, params: registryParams ?? paramMap[type] ?? {} };
    } catch { return { type:"general", params:{} }; }
  };

  const parseResponse = (text) => {
    const navMatch = text.match(/\[NAVIGATE:(\w+)\]/);
    const clean = text
      .replace(/\[NAVIGATE:\w+\]/g, "")
      .replace(/\[ACTION:\w+[^\]]*\]/g, "") // strip any stray action tags
      .replace(/\s{2,}/g, " ").trim();
    return { clean, navigate: navMatch?.[1] || null };
  };

  const executeAction = async (action, msgId) => {
    try {
      let result = null;
      if (action.type === "write_content") {
        const { type = "blog", title } = action.params;
        const typeLabel = CW_TYPE_LABEL[type] || "Blog Post";
        const text = await ai(
          `Write a ${typeLabel} titled: "${title}" for ${biz}, a ${cat}${loc}.
Length: 500-800 words. Tone: professional, engaging, authentic. No markdown headers in the output — use plain paragraphs and natural structure.
Make it SEO-friendly and specific to the business's niche.`,
          "You are an award-winning marketing copywriter. Write compelling content that builds brand authority and drives customer action.",
          false, 1800
        );
        result = { kind: "content", contentType: type, title, text };
        await logActivity("content", `AI wrote: ${title}`, { type, source: "assistant" });
      } else if (action.type === "write_social") {
        const { platform = "instagram", topic } = action.params;
        const text = await ai(
          `Write a ${platform} post for ${biz} (${cat}${loc}) about: ${topic}.
Be platform-native. For Instagram: visual hook + storytelling + hashtags. For Facebook: conversational + CTA. For LinkedIn: professional insight. For TikTok: punchy hook + trend. For Twitter/X: concise + punchy.
Include 3-5 relevant hashtags at the end.`,
          "You are a social media strategist. Write posts that feel native, drive engagement, and reflect authentic brand voice.",
          false, 600
        );
        result = { kind: "social", platform, topic, text };
        await logActivity("content", `AI social post for ${platform}`, { platform, topic, source: "assistant" });
      } else if (action.type === "save_keywords") {
        const { name = "My Keywords" } = action.params;
        const keywords = lastKeywordsRef.current;
        if (!keywords?.length) throw new Error("No keywords to save — generate keywords first.");
        const listEntry = {
          id: `kl_${Date.now()}`,
          name,
          createdAt: new Date().toISOString(),
          location: profile?.city || "Local",
          keywords: keywords.map(k => ({
            keyword: k.keyword, volume: null, competition: null,
            intent: k.intent, cpc: null, source: "ai-chat",
          })),
        };
        const existing = await store.get("keyword-lists") || [];
        await store.set("keyword-lists", [listEntry, ...existing]);
        window.dispatchEvent(new CustomEvent("keyword-list-saved", { detail: listEntry }));
        await logActivity("research", `Saved keyword list: "${name}"`, { source: "assistant" });
        result = { kind: "keywords_saved", name, count: keywords.length };
      } else if (action.type === "suggest_keywords") {
        const { focus } = action.params;
        const raw = await ai(
          `Generate the 10 best SEO and Google Ads keywords for ${biz}, a ${cat}${loc}.
Focus: ${focus || "general promotion and local discovery"}.

IMPORTANT: Every object MUST include all 7 fields — keyword, intent, reason, priority, volume, difficulty, opportunity. Never omit any field.

Return ONLY a JSON array. Example of correct format:
[
  {"keyword":"chinese restaurant suwanee ga","intent":"Local","reason":"High-intent local search combining cuisine and location","priority":"High","volume":"1.2k","difficulty":"Low","opportunity":"High"},
  {"keyword":"dim sum near me","intent":"Local","reason":"Voice-search term for discovery","priority":"High","volume":"880","difficulty":"Low","opportunity":"High"}
]

Volume estimates: local brand = 100-500, local category = 500-5k, city-level = 1k-20k, broad = 5k+.
Difficulty: Low = long-tail/local specific, Med = moderate competition, High = generic head terms.
Opportunity: High = high vol + low/med diff, Med = balanced, Low = hard or low-volume.
Prioritize local high-intent terms. Mix broad and long-tail. No duplicates.`,
          "You are a local SEO and paid search expert. Return only a valid JSON array. Every object must have all 7 fields including volume, difficulty, and opportunity with realistic estimated values.",
          false, 1200
        );
        let keywords = [];
        try {
          const match = raw.match(/\[[\s\S]*\]/);
          keywords = JSON.parse(match ? match[0] : raw);
          // Ensure every keyword has the required fields
          keywords = keywords.map(k => ({
            ...k,
            volume:     k.volume     || "—",
            difficulty: k.difficulty || "Med",
            opportunity:k.opportunity|| "Med",
          }));
        } catch { keywords = [{ keyword: raw, intent: "General", reason: "", priority: "High", volume:"—", difficulty:"Med", opportunity:"Med" }]; }
        lastKeywordsRef.current = keywords;
        result = { kind: "keywords", focus, keywords };
        await logActivity("research", `AI keyword suggestions: ${focus || "general"}`, { source: "assistant" });
      } else if (action.type === "save_topics") {
        const { name = "My Topics" } = action.params;
        const topics = lastTopicsRef.current;
        if (!topics?.length) throw new Error("No topics to save — generate topics first.");
        const listEntry = {
          id: `tl_${Date.now()}`, name,
          createdAt: new Date().toISOString(),
          topics: topics.map(t => ({ ...t, savedAt: new Date().toISOString() })),
        };
        const existing = await store.get("topic-lists") || [];
        await store.set("topic-lists", [listEntry, ...existing]);
        await logActivity("content", `Saved topic list: "${name}"`, { source: "assistant" });
        result = { kind: "topics_saved", name, count: topics.length };
      } else if (action.type === "suggest_topics") {
        const { focus } = action.params;
        const raw = await ai(
          `Generate the 8 best content topics for ${biz}, a ${cat}${loc}.
Focus: ${focus || "general content marketing and customer engagement"}.

Return ONLY a valid JSON array, no markdown, no explanation. Format:
[{"title":"exact post/article title","type":"Blog Post|Email|Social Post|Video Script","description":"one sentence on why this resonates with their audience","angle":"Educational|Promotional|Storytelling|Seasonal"}]

Prioritize titles that are specific, interesting, and SEO-friendly. Mix content types. No duplicates.`,
          "You are a content strategist for local businesses. Return only valid JSON.",
          false, 900
        );
        let topics = [];
        try {
          const match = raw.match(/\[[\s\S]*\]/);
          topics = JSON.parse(match ? match[0] : raw);
        } catch { topics = [{ title: raw, type: "Blog Post", description: "", angle: "Educational" }]; }
        lastTopicsRef.current = topics;
        result = { kind: "topics", focus, topics };
        await logActivity("content", `AI topic suggestions: ${focus || "general"}`, { source: "assistant" });
      } else if (action.type === "draft_campaign") {
        const { type = "email", subject, goal = "engage guests" } = action.params;
        const text = await ai(
          `Write a ${type === "sms" ? "SMS" : "email"} marketing campaign for ${biz} (${cat}${loc}).
${type === "email" ? `Subject: ${subject || "Special offer from " + biz}` : ""}
Goal: ${goal}. ${type === "sms" ? "Max 160 characters. Include business name and opt-out." : "Include: greeting, 3 body paragraphs, clear CTA, and professional sign-off."}`,
          "You are a direct-response marketing expert specialising in local business campaigns.",
          false, type === "sms" ? 300 : 1000
        );
        result = { kind: "campaign", campaignType: type, subject, goal, text };
        await logActivity("campaign", `AI drafted ${type} campaign`, { goal, source: "assistant" });
      } else if (action.type === "create_campaign") {
        const { template: tplId = "grand_opening", topic: tplTopic } = action.params;
        // Look up in ALL_TEMPLATES first, then fall back to DEFAULT_OUTCOMES (any niche)
        const allOutcomes = Object.values(DEFAULT_OUTCOMES).flat();
        let tpl = ALL_TEMPLATES.find(t => t.id === tplId)
          || allOutcomes.find(o => o.id === tplId)
          || ALL_TEMPLATES.find(t => t.id === "grand_opening");
        // Normalise DEFAULT_OUTCOMES entries to campaign format
        if (!tpl.schedule) tpl = { ...tpl, name: tpl.title, schedule: { type:"once", time:"09:00", day:1, date:1 } };
        const topic = tplTopic || tpl.topicFn(biz, {});
        const campaign = {
          id: Date.now(),
          name: `${biz} — ${tpl.name || tpl.title}`,
          restaurant: biz,
          goal: tpl.goal,
          topic,
          tone: tpl.tone,
          schedule: tpl.schedule,
          actions: Object.fromEntries(tpl.channels.map(c => [c, true])),
          status: "active",
          runs: [],
          created: new Date().toLocaleDateString(),
          lastRun: null,
          source: "assistant",
        };
        const enabledActions = ACTIONS.filter(a => campaign.actions[a.id]);
        const results = {};
        const completedChannels = [];
        abortCampaignRef.current = false;
        // Generate content for each channel with live progress and abort support
        for (const channelAction of enabledActions) {
          if (abortCampaignRef.current) break;
          setMessages(prev => prev.map(m => m.id === msgId ? {
            ...m, actionLoading: false,
            actionResult: {
              kind: "campaign_generating",
              campaign, templateName: tpl.name || tpl.title, channels: tpl.channels,
              completedChannels: [...completedChannels],
              currentChannel: channelAction.label,
              total: enabledActions.length,
            }
          } : m));
          const content = await ai(
            getCampaignPrompt(channelAction.id, campaign),
            "You are an expert marketing AI running an automated campaign. Generate high-quality, ready-to-publish content specific to this restaurant and campaign."
          );
          if (abortCampaignRef.current) break;
          results[channelAction.id] = content;
          completedChannels.push(channelAction.id);
        }
        const stopped = abortCampaignRef.current;
        abortCampaignRef.current = false;
        // Save campaign with whatever channels completed (even if stopped early)
        const run = { id: Date.now(), timestamp: new Date().toISOString(), campaignName: campaign.name, restaurant: biz, results, actionCount: completedChannels.length };
        campaign.lastRun = new Date().toISOString();
        campaign.runs = [run];
        const existing = await store.get("campaigns") || [];
        await store.set("campaigns", [campaign, ...existing]);
        window.dispatchEvent(new CustomEvent("campaign-created-by-assistant", { detail: { campaign } }));
        const tplName = tpl.name || tpl.title;
        result = { kind: "campaign_created", campaign, templateName: tplName, channels: tpl.channels, channelCount: completedChannels.length, stopped };
        await logActivity("campaign", `AI created ${tplName} campaign (${completedChannels.length} channels${stopped ? ", stopped early" : ""})`, { source: "assistant" });
      } else if (action.type === "respond_review") {
        const allReviews = await store.get("reviews") || SEED_REVIEWS;
        const unresponded = allReviews.filter(r => !r.responded);
        const target = (action.params.target || "latest").toLowerCase();
        let targets = [];
        if (target === "all")          targets = unresponded.slice(0, 5);
        else if (target === "negative") targets = unresponded.filter(r => r.rating <= 3).slice(0, 5);
        else if (target === "latest")   targets = unresponded.slice(0, 3);
        else {
          const match = unresponded.find(r => (r.reviewer||r.reviewerName||"").toLowerCase().includes(target));
          targets = match ? [match] : unresponded.slice(0, 3);
        }
        if (!targets.length) {
          result = { kind: "review_reply", reviews: [], allResponded: true };
        } else {
          const withReplies = await Promise.all(targets.map(async r => {
            const name = r.reviewer || r.reviewerName || "Guest";
            const reply = await ai(
              `Write a professional, warm, and genuine response to this ${r.rating}-star Google review for ${biz} (${cat}${loc}).\n\nReviewer: ${name}\nReview: "${r.text || "No text provided"}"\n\nGuidelines:\n- Thank them by name\n- Address specific points they raised (positive or negative)\n- If negative: empathize, apologize, invite them back, offer to make it right\n- If positive: express genuine gratitude, reinforce what they loved\n- Keep under 120 words\n- Sound like a real business owner, not corporate PR\n- Do NOT start with "Thank you for your review"\n\nReturn ONLY the reply text, no quotes, no labels.`,
              `You are ${biz}'s owner writing heartfelt, professional Google review responses. Sound human and genuine.`,
              false, 350
            );
            return { ...r, generatedReply: reply.trim() };
          }));
          result = { kind: "review_reply", reviews: withReplies };
          await logActivity("reviews", `AI generated ${withReplies.length} review reply drafts`, { source: "assistant" });
        }
      } else if (action.type === "summarize_reviews") {
        const allReviews = await store.get("reviews") || SEED_REVIEWS;
        const total = allReviews.length;
        const avg = total ? (allReviews.reduce((a,r) => a+r.rating, 0) / total).toFixed(1) : 0;
        const byRating = [5,4,3,2,1].map(n => ({ stars: n, count: allReviews.filter(r => r.rating === n).length }));
        const positives = allReviews.filter(r => r.rating >= 4).map(r => r.text).filter(Boolean).slice(0, 5).join("\n");
        const negatives = allReviews.filter(r => r.rating <= 2).map(r => r.text).filter(Boolean).slice(0, 5).join("\n");
        const summary = await ai(
          `Analyze these Google reviews for ${biz} (${cat}${loc}) and give a concise summary.\n\nStats: ${total} reviews, avg ${avg}★\n\nPositive reviews:\n${positives || "None"}\n\nNegative reviews:\n${negatives || "None"}\n\nProvide:\n1. Overall sentiment (1 sentence)\n2. Top 3 things customers love\n3. Top 2-3 recurring complaints or areas to improve\n4. One actionable tip to improve the rating\n\nBe specific and use actual phrases from the reviews. Keep it under 200 words.`,
          "You are a business intelligence analyst. Be specific, data-driven, and actionable.",
          false, 600
        );
        result = { kind: "review_summary", summary, total, avg, byRating, unresponded: allReviews.filter(r => !r.responded).length };
        await logActivity("reviews", "AI analyzed review sentiment", { source: "assistant" });
      } else if (action.type === "request_review") {
        const channel = action.params.channel || "message";
        const message = await ai(
          `Write a short, friendly review request ${channel} for ${biz} (${cat}${loc}).\n\nFormat: ${channel === "email" ? "Email with subject line then body" : channel === "sms" ? "SMS under 160 characters" : "Short personal message (2-3 sentences)"}\n\nTone: warm, personal, not pushy. Thank them for visiting. Make it easy — just ask them to leave a quick Google review.\nDo NOT include a URL placeholder.\nReturn ONLY the message text.`,
          "You are a customer success expert writing personalized review request messages.",
          false, 300
        );
        result = { kind: "review_request", channel, message: message.trim() };
        await logActivity("reviews", `AI generated ${channel} review request`, { source: "assistant" });
      } else if (action.type === "add_contact") {
        const { name = "", contact = "" } = action.params;
        const isEmail = contact.includes("@");
        const newContact = {
          id: Date.now(),
          name: name || "New Contact",
          email: isEmail ? contact : "",
          phone: !isEmail ? contact : "",
          type: "customer",
          source: "assistant",
          notes: "",
          addedAt: new Date().toLocaleDateString(),
        };
        const existing = await store.get("crm-contacts") || [];
        await store.set("crm-contacts", [newContact, ...existing]);
        window.dispatchEvent(new CustomEvent("crm-updated"));
        await logActivity("crm", `AI added contact: ${newContact.name}`, { source: "assistant" });
        result = { kind: "crm_contact_added", contact: newContact };
      } else if (action.type === "find_contact") {
        const { query = "" } = action.params;
        const all = await store.get("crm-contacts") || [];
        const q = query.toLowerCase();
        const matches = all.filter(c =>
          (c.name||"").toLowerCase().includes(q) ||
          (c.email||"").toLowerCase().includes(q) ||
          (c.phone||"").toLowerCase().includes(q)
        ).slice(0, 5);
        result = { kind: "crm_contact_found", contacts: matches, query };
      } else if (action.type === "add_note") {
        const { name = "", note = "" } = action.params;
        const all = await store.get("crm-contacts") || [];
        const idx = all.findIndex(c => (c.name||"").toLowerCase().includes(name.toLowerCase()));
        if (idx === -1) throw new Error(`Contact "${name}" not found.`);
        const noteWithDate = `[${new Date().toLocaleDateString()}] ${note}`;
        const updated = all.map((c, i) => i === idx ? { ...c, notes: c.notes ? `${c.notes}\n${noteWithDate}` : noteWithDate } : c);
        await store.set("crm-contacts", updated);
        window.dispatchEvent(new CustomEvent("crm-updated"));
        await logActivity("crm", `AI added note to ${all[idx].name}`, { source: "assistant" });
        result = { kind: "crm_note_added", contact: updated[idx], note };
      } else if (action.type === "campaign_status") {
        const all = await store.get("campaigns") || [];
        const active = all.filter(c => c.status === "active");
        const draft  = all.filter(c => c.status === "draft");
        const paused = all.filter(c => c.status === "paused");
        result = { kind: "campaign_overview", campaigns: all.slice(0, 8), active: active.length, draft: draft.length, paused: paused.length, total: all.length };
      } else if (action.type === "pause_campaign") {
        const { name = "" } = action.params;
        const all = await store.get("campaigns") || [];
        const idx = all.findIndex(c => (c.name||"").toLowerCase().includes(name.toLowerCase()) && c.status !== "paused");
        if (idx === -1) throw new Error(`Active campaign "${name}" not found.`);
        const updated = all.map((c, i) => i === idx ? { ...c, status: "paused" } : c);
        await store.set("campaigns", updated);
        window.dispatchEvent(new CustomEvent("campaigns-updated"));
        await logActivity("campaign", `AI paused campaign: ${all[idx].name}`, { source: "assistant" });
        result = { kind: "campaign_updated", action: "paused", campaign: updated[idx] };
      } else if (action.type === "resume_campaign") {
        const { name = "" } = action.params;
        const all = await store.get("campaigns") || [];
        const idx = all.findIndex(c => (c.name||"").toLowerCase().includes(name.toLowerCase()) && c.status === "paused");
        if (idx === -1) throw new Error(`Paused campaign "${name}" not found.`);
        const updated = all.map((c, i) => i === idx ? { ...c, status: "active" } : c);
        await store.set("campaigns", updated);
        window.dispatchEvent(new CustomEvent("campaigns-updated"));
        await logActivity("campaign", `AI resumed campaign: ${all[idx].name}`, { source: "assistant" });
        result = { kind: "campaign_updated", action: "resumed", campaign: updated[idx] };
      } else if (action.type === "add_reservation") {
        const { name, date, time, party } = action.params;
        if (!name) throw new Error("I need a name to create the booking.");
        const booking = {
          id: Date.now(),
          name: name || "Guest",
          email: "", phone: "",
          date: date || new Date().toISOString().split("T")[0],
          time: time || "19:00",
          party: party || "2",
          type: "Reservation",
          notes: "",
          status: "confirmed",
          createdAt: new Date().toLocaleDateString(),
        };
        const all = await store.get("reservations") || [];
        await store.set("reservations", [booking, ...all]);
        window.dispatchEvent(new CustomEvent("reservations-updated"));
        result = { kind: "reservation_added", booking };
        await logActivity("reservations", `AI booked: ${booking.name} on ${booking.date} at ${booking.time}`, { source: "assistant" });
      } else if (action.type === "reservation_status") {
        const all = await store.get("reservations") || [];
        const today = new Date().toISOString().split("T")[0];
        const todayList = all.filter(r => r.status === "confirmed" && r.date === today);
        const upcoming = all.filter(r => r.status === "confirmed" && r.date > today).slice(0, 8);
        result = { kind: "reservation_overview", todayList, upcoming, total: all.length };
      } else if (action.type === "cancel_reservation") {
        const target = (action.params.target || "").toLowerCase();
        const all = await store.get("reservations") || [];
        const match = all.find(r => r.status === "confirmed" && (
          r.name?.toLowerCase().includes(target) ||
          r.date?.includes(target)
        ));
        if (!match) throw new Error(`No confirmed booking found matching "${action.params.target}".`);
        const updated = all.map(r => r.id === match.id ? { ...r, status: "cancelled" } : r);
        await store.set("reservations", updated);
        window.dispatchEvent(new CustomEvent("reservations-updated"));
        result = { kind: "reservation_cancelled", booking: match };
        await logActivity("reservations", `AI cancelled: ${match.name} on ${match.date}`, { source: "assistant" });
      } else if (action.type === "create_gbp_post") {
        const topic = action.params.topic || "business update";
        const text = await ai(
          `Write a Google Business Profile post for ${biz} (${cat}${loc}) about: ${topic}.

Requirements:
- Max 1,400 characters (IMPORTANT — count carefully)
- No hashtags
- Strong opening hook (first line is what customers see in search)
- Include specific details relevant to the business
- End with a clear CTA: call us, visit us, book online, etc.
- Conversational and local — sound like a real business owner
- Do NOT use markdown

Return ONLY the post text.`,
          "You are a Google Business Profile expert writing posts that drive local customer action.",
          false, 500
        );
        result = { kind: "gbp_post", topic, text: text.trim() };
        await logActivity("content", `AI GBP post: ${topic}`, { source: "assistant" });
      } else if (action.type === "gbp_qa") {
        const topic = action.params.topic || "general";
        const raw = await ai(
          `Generate 5 common customer Q&A pairs for ${biz} (${cat}${loc})${topic !== "general" ? ` focused on: ${topic}` : ""}.

Cover things customers actually ask: hours, parking, reservations, menu/services, pricing, dietary options, accessibility, etc.

Return ONLY a JSON array:
[{"q":"Question?","a":"Answer."}]

Keep answers under 100 words each. Be specific to this business.`,
          "You are a local business expert generating helpful Q&A for a Google Business Profile.",
          false, 800
        );
        let pairs = [];
        try { const m = raw.match(/\[[\s\S]*\]/); pairs = JSON.parse(m ? m[0] : raw); } catch(e) { pairs = []; }
        result = { kind: "gbp_qa_pairs", pairs, topic };
        await logActivity("content", `AI GBP Q&A: ${topic}`, { source: "assistant" });
      }
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, actionLoading: false, actionResult: result } : m));
    } catch (e) {
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, actionLoading: false, actionError: e.message } : m));
    }
  };

  const saveContent = async (result, msgId) => {
    try {
      const lib = await store.get("content-library") || [];
      const item = { id: Date.now(), type: result.contentType || "blog", title: result.title, topic: result.title, content: result.text, business: profile?.name || "", tone: "professional", length: "medium", savedAt: new Date().toISOString(), source: "assistant" };
      await store.set("content-library", [item, ...lib]);
      // Remove matching item from the content queue
      const norm = t => t?.toLowerCase().trim().slice(0, 60);
      const q = await store.get("content-ideas") || [];
      const match = q.find(i => norm(i.title) === norm(result.title));
      if (match) {
        await store.set("content-ideas", q.filter(i => i.id !== match.id));
        window.dispatchEvent(new CustomEvent("content-queue-updated"));
      }
      window.dispatchEvent(new CustomEvent("content-library-updated", { detail: { openItem: item } }));
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, actionSaved: true, savedItem: item } : m));
    } catch (e) {
      console.error("saveContent failed:", e);
    }
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    setInput("");
    const userMsg = { role:"user", content: text };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setLoading(true);
    try {
      // Run classifier and chat in parallel — classifier picks the action, chat writes the reply
      const [intent, response] = await Promise.all([
        classifyIntent(text),
        aiChat(nextMessages.map(m => ({ role: m.role, content: m.content })), buildChatSystem(), 900),
      ]);
      const { clean, navigate: chatNav } = parseResponse(response);
      const msgId = `${Date.now()}-${Math.random().toString(36).slice(2,6)}`;

      // Classifier is authoritative for actions; these types have no card action or are handled separately
      const isAction = !["general","navigate","website_edit"].includes(intent.type);
      const action   = isAction ? { type: intent.type, params: intent.params } : null;

      // Navigation: classifier wins if it said navigate, otherwise use [NAVIGATE:] from chat
      const navigate = intent.type === "navigate" ? intent.params.module : chatNav;

      setMessages(prev => [...prev, { id: msgId, role:"assistant", content: clean, navigate, action, actionLoading: !!action, actionResult: null, actionError: null, actionSaved: false }]);
      if (action) setTimeout(() => executeAction(action, msgId), 80);

      // Bridge to Website Builder — fires when classifier identifies a website edit intent
      if (mod === "website" && typeof window._wbApplyChange === "function" && intent.type === "website_edit") {
        window._wbApplyChange(text);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role:"assistant", content:"Sorry, something went wrong. Please try again.", error:true }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  return (
    <div style={{ width:panelWidth, borderLeft:`1px solid ${C.border}`, background:C.s1, display:"flex", flexDirection:"column", flexShrink:0, height:"100%", position:"relative" }}>
      {/* Drag handle */}
      <div onMouseDown={onDragStart}
        style={{ position:"absolute", left:0, top:0, bottom:0, width:5, cursor:"col-resize", zIndex:10, display:"flex", alignItems:"center", justifyContent:"center" }}
        title="Drag to resize">
        <div style={{ width:3, height:40, borderRadius:2, background:C.border, opacity:0.6, transition:"opacity 0.15s" }}
          onMouseOver={e => e.currentTarget.style.opacity="1"}
          onMouseOut={e  => e.currentTarget.style.opacity="0.6"} />
      </div>
      {/* Header */}
      <div style={{ padding:"14px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
        <div style={{ width:30, height:30, borderRadius:"50%", background:`${C.accent}22`, border:`1px solid ${C.accent}50`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>✦</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{wlConfig?.app_name||"Kurb AI"} Assistant</div>
          <div style={{ fontSize:10, color:C.success, display:"flex", alignItems:"center", gap:4, marginTop:1 }}>
            <div style={{ width:5, height:5, borderRadius:"50%", background:C.success, flexShrink:0 }} />
            Ask me anything
          </div>
        </div>
        <button onClick={() => setShowHistory(v => !v)} title="Chat history"
          style={{ background:"none", border:"none", color: showHistory ? C.accent : C.t3, cursor:"pointer", fontSize:14, lineHeight:1, padding:4 }}>◷</button>
        <button onClick={onClose} style={{ background:"none", border:"none", color:C.t3, cursor:"pointer", fontSize:18, lineHeight:1, padding:4 }}>✕</button>
      </div>

      {/* History panel */}
      {showHistory && (
        <div style={{ flex:1, overflowY:"auto", background:C.bg, borderTop:`1px solid ${C.border}` }}>
          <div style={{ padding:"12px 14px 8px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <span style={{ fontSize:12, fontWeight:700, color:C.text }}>Chat History</span>
            <button onClick={startNewChat} style={{ fontSize:11, padding:"4px 10px", background:`${C.accent}18`, border:`1px solid ${C.accent}40`, borderRadius:6, color:C.accent, cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>+ New Chat</button>
          </div>
          {history.length === 0 ? (
            <div style={{ padding:"24px 14px", textAlign:"center", fontSize:12, color:C.t3 }}>No past conversations yet.</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:1 }}>
              {history.map(conv => (
                <button key={conv.id} onClick={() => { setViewingConv(conv); setShowHistory(false); }}
                  style={{ padding:"10px 14px", background:"transparent", border:"none", borderBottom:`1px solid ${C.border}20`, cursor:"pointer", fontFamily:"inherit", textAlign:"left", width:"100%", transition:"background 0.1s" }}
                  onMouseOver={e => e.currentTarget.style.background = C.s2}
                  onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                  <div style={{ fontSize:12, color:C.text, marginBottom:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{conv.title}</div>
                  <div style={{ fontSize:10, color:C.t3 }}>{fmtRelDate(conv.date)} · {conv.messages.length} messages</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Viewing past conversation */}
      {viewingConv && !showHistory && (
        <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
          <div style={{ padding:"8px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8, background:C.s2, flexShrink:0 }}>
            <button onClick={() => setViewingConv(null)} style={{ background:"none", border:"none", color:C.accent, fontSize:12, cursor:"pointer", fontFamily:"inherit", fontWeight:600 }}>← Back</button>
            <span style={{ fontSize:11, color:C.t3, flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{fmtRelDate(viewingConv.date)} — {viewingConv.title}</span>
            <button onClick={() => { setMessages(viewingConv.messages); setViewingConv(null); }}
              style={{ fontSize:11, padding:"3px 8px", background:`${C.accent}18`, border:`1px solid ${C.accent}40`, borderRadius:5, color:C.accent, cursor:"pointer", fontFamily:"inherit" }}>Restore</button>
          </div>
          <div style={{ flex:1, overflowY:"auto", padding:"12px", display:"flex", flexDirection:"column", gap:8 }}>
            {viewingConv.messages.map((m, i) => (
              <div key={i} style={{ display:"flex", flexDirection:"column", alignItems: m.role==="user"?"flex-end":"flex-start", gap:4 }}>
                {m.role==="assistant" && <div style={{ fontSize:10, color:C.t3, marginLeft:4 }}><span style={{ color:C.accent }}>✦</span> {wlConfig?.app_name||"Kurb AI"}</div>}
                <div style={{ maxWidth:"90%", padding:"8px 11px", borderRadius:"5px", background: m.role==="user"?"linear-gradient(135deg, #3eade1 0%, #3e6be2 100%)":C.s2, color: m.role==="user"?"#fff":C.text, fontSize:12, lineHeight:1.6 }}>{m.role==="assistant" ? renderMarkdown(m.content) : m.content}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div style={{ flex:1, overflowY:"auto", padding:"14px 12px", display: (showHistory || viewingConv) ? "none" : "flex", flexDirection:"column", gap:10 }}>
        {messages.length === 0 ? (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ textAlign:"center", marginBottom:8 }}>
              <div style={{ fontSize:28, marginBottom:8 }}>✦</div>
              <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:3 }}>Hi! I'm your AI marketing assistant.</div>
              <div style={{ fontSize:11, color:C.t3, lineHeight:1.5 }}>I can answer questions, navigate you to any module, <strong style={{ color:C.t2 }}>and take real actions</strong> — write content, draft campaigns, create social posts, and more.</div>
            </div>
            {AI_SUGGESTIONS.map(s => (
              <button key={s.text} onClick={() => send(s.text)}
                style={{ padding:"9px 12px", background:C.s1, border:`1px solid ${C.border}`, borderRadius:5, color:C.t2, fontSize:12, fontFamily:"inherit", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:8, transition:"all 0.15s", fontWeight:500 }}
                onMouseOver={e => { e.currentTarget.style.background="linear-gradient(135deg, #7A5AF8 0%, #6B21A8 100%)"; e.currentTarget.style.color="#fff"; e.currentTarget.style.borderColor="transparent"; }}
                onMouseOut={e  => { e.currentTarget.style.background=C.s1; e.currentTarget.style.color=C.t2; e.currentTarget.style.borderColor=C.border; }}>
                <span style={{ fontSize:14, flexShrink:0 }}>{s.icon}</span>
                <span>{s.text}</span>
              </button>
            ))}
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={m.id || i} style={{ display:"flex", flexDirection:"column", alignItems: m.role==="user" ? "flex-end" : "flex-start", gap:5 }}>
              {m.role === "assistant" && (
                <div style={{ fontSize:10, color:C.t3, marginLeft:4, display:"flex", alignItems:"center", gap:4 }}>
                  <span style={{ color:C.accent }}>✦</span> {wlConfig?.app_name||"Kurb AI"}
                </div>
              )}
              {m.content && (
                <div style={{ maxWidth:"90%", padding:"9px 12px", borderRadius:"5px", background: m.role==="user" ? "linear-gradient(135deg, #3eade1 0%, #3e6be2 100%)" : C.s2, color: m.role==="user" ? "#fff" : C.text, fontSize:12, lineHeight:1.65, wordBreak:"break-word" }}>
                  {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
                </div>
              )}
              {/* Navigate button */}
              {m.navigate && NAV_LABELS[m.navigate] && (
                <button onClick={() => onNavigate(m.navigate)}
                  style={{ fontSize:12, padding:"8px 16px", background:"linear-gradient(135deg, #7A5AF8 0%, #6B21A8 100%)", border:"none", borderRadius:5, color:"#fff", cursor:"pointer", fontFamily:"inherit", fontWeight:700, display:"flex", alignItems:"center", gap:6, alignSelf:"flex-start", transition:"all 0.12s" }}>
                  → Open {NAV_LABELS[m.navigate]}
                </button>
              )}
              {/* Action loading */}
              {m.actionLoading && (
                <div style={{ maxWidth:"90%", padding:"12px 14px", background:C.s2, border:`1px solid ${C.border}`, borderRadius:"3px 14px 14px 14px", display:"flex", alignItems:"center", gap:8 }}>
                  <div style={{ width:14, height:14, border:`2px solid ${C.accent}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite", flexShrink:0 }} />
                  <span style={{ fontSize:12, color:C.t2 }}>Working on it…</span>
                </div>
              )}
              {/* Action error */}
              {m.actionError && (
                <div style={{ maxWidth:"90%", padding:"9px 12px", background:`${C.danger}12`, border:`1px solid ${C.danger}40`, borderRadius:"3px 14px 14px 14px", fontSize:12, color:C.danger }}>
                  Failed: {m.actionError}
                </div>
              )}
              {/* Action result: content */}
              {m.actionResult?.kind === "content" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:"#6366f118", color:"#818cf8", fontWeight:700 }}>{CW_TYPE_LABEL[m.actionResult.contentType] || "Content"}</span>
                    <span style={{ fontSize:12, fontWeight:600, color:C.text, flex:1, minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.actionResult.title}</span>
                  </div>
                  <div style={{ padding:"10px 12px", fontSize:12, color:C.t2, lineHeight:1.65, maxHeight:140, overflowY:"auto" }}>
                    {m.actionResult.text.slice(0, 400)}{m.actionResult.text.length > 400 ? "…" : ""}
                  </div>
                  <div style={{ padding:"8px 12px", borderTop:`1px solid ${C.border}`, display:"flex", gap:6 }}>
                    {m.actionSaved ? (
                      <span style={{ fontSize:11, color:C.success, fontWeight:600 }}>✓ Saved to Content Library</span>
                    ) : (
                      <button onClick={() => saveContent(m.actionResult, m.id)}
                        style={{ ...S.btn, fontSize:11, padding:"5px 12px" }}>Save to Library</button>
                    )}
                    <button onClick={() => {
                        if (m.savedItem) window.dispatchEvent(new CustomEvent("content-library-updated", { detail: { openItem: m.savedItem } }));
                        onNavigate("content"); onClose();
                      }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 10px" }}>Open Content →</button>
                  </div>
                </div>
              )}
              {/* Action result: keywords */}
              {m.actionResult?.kind === "keywords" && (() => {
                const parseVol = (v) => {
                  if (!v || v === "—") return 0;
                  const s = String(v).toLowerCase().replace(/[^0-9.k]/g,"");
                  return s.endsWith("k") ? parseFloat(s) * 1000 : parseFloat(s) || 0;
                };
                const kws = [...(m.actionResult.keywords || [])].sort((a,b) => parseVol(b.volume) - parseVol(a.volume));
                const INTENT_COLOR = { Local:"#10b981", Commercial:"#6366f1", Informational:"#f59e0b", Branded:"#8b5cf6" };
                const DIFF_COLOR   = { Low:C.success, Med:"#f59e0b", Medium:"#f59e0b", High:C.danger };
                const OPP_COLOR    = { High:C.success, Med:"#f59e0b", Medium:"#f59e0b", Low:C.t3 };
                const saveState = kwSave[m.id] || {};
                const doSave = async () => {
                  const name = (saveState.name || "").trim() || `Keywords — ${new Date().toLocaleDateString()}`;
                  const listEntry = {
                    id: `kl_${Date.now()}`, name,
                    createdAt: new Date().toISOString(),
                    location: profile?.city || "Local",
                    keywords: kws.map(k => ({ keyword: k.keyword, volume: k.volume||null, competition: k.difficulty||null, intent: k.intent, cpc: null, opportunity: k.opportunity||null, source: "ai-chat" })),
                  };
                  const existing = await store.get("keyword-lists") || [];
                  await store.set("keyword-lists", [listEntry, ...existing]);
                  window.dispatchEvent(new CustomEvent("keyword-list-saved", { detail: listEntry }));
                  setKwSave(p => ({ ...p, [m.id]: { ...p[m.id], saved: true, savedName: name } }));
                };
                return (
                  <div style={{ width:"100%", background:C.s1, border:`1px solid #c4b5fd`, borderRadius:10, overflow:"hidden", boxShadow:S.card.boxShadow }}>
                    {/* Header */}
                    <div style={{ padding:"10px 14px", borderBottom:`1px solid #c4b5fd`, display:"flex", alignItems:"center", gap:8, background:"#ede9ff" }}>
                      <span style={{ fontSize:15 }}>🔍</span>
                      <div style={{ fontSize:12, fontWeight:600, color:"#6B21A8" }}>Keyword Suggestions</div>
                      <span style={{ fontSize:10, fontWeight:600, color:"#7c3aed", marginLeft:"auto", background:"#ddd6fe", padding:"1px 8px", borderRadius:10 }}>{kws.length} found</span>
                    </div>
                    {/* Column headers */}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 36px 36px 36px", gap:8, padding:"6px 14px 4px", borderBottom:`1px solid ${C.border}` }}>
                      <span style={{ fontSize:9, fontWeight:700, color:C.t3, textTransform:"uppercase", letterSpacing:"0.06em" }}>Keyword</span>
                      <span style={{ fontSize:9, fontWeight:700, color:C.t3, textTransform:"uppercase", letterSpacing:"0.06em", textAlign:"center" }}>VOL</span>
                      <span style={{ fontSize:9, fontWeight:700, color:C.t3, textTransform:"uppercase", letterSpacing:"0.06em", textAlign:"center" }}>DIFF</span>
                      <span style={{ fontSize:9, fontWeight:700, color:C.t3, textTransform:"uppercase", letterSpacing:"0.06em", textAlign:"center" }}>OPP</span>
                    </div>
                    {/* Rows */}
                    <div style={{ maxHeight:340, overflowY:"auto" }}>
                      {kws.map((k, i) => (
                        <div key={i} style={{ padding:"9px 14px", borderBottom:`1px solid ${C.border}20`, display:"grid", gridTemplateColumns:"1fr 36px 36px 36px", gap:8, alignItems:"center" }}>
                          <div style={{ minWidth:0 }}>
                            <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:2 }}>{k.keyword}</div>
                            <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                              <span style={{ fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:6, background:`${INTENT_COLOR[k.intent]||"#6366f1"}15`, color:INTENT_COLOR[k.intent]||"#6366f1", textTransform:"uppercase" }}>{k.intent}</span>
                              {k.reason && <span style={{ fontSize:10, color:C.t3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{k.reason}</span>}
                            </div>
                          </div>
                          {/* VOL */}
                          <div style={{ textAlign:"center" }}>
                            <span style={{ fontSize:11, fontWeight:700, color:C.text }}>{k.volume || "—"}</span>
                          </div>
                          {/* DIFF */}
                          <div style={{ textAlign:"center" }}>
                            <span style={{ fontSize:10, fontWeight:700, padding:"2px 5px", borderRadius:5, background:`${DIFF_COLOR[k.difficulty]||C.t3}18`, color:DIFF_COLOR[k.difficulty]||C.t3 }}>{k.difficulty || "—"}</span>
                          </div>
                          {/* OPP */}
                          <div style={{ textAlign:"center" }}>
                            <span style={{ fontSize:10, fontWeight:700, padding:"2px 5px", borderRadius:5, background:`${OPP_COLOR[k.opportunity]||C.t3}18`, color:OPP_COLOR[k.opportunity]||C.t3 }}>{k.opportunity || "—"}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Save UI */}
                    <div style={{ padding:"10px 12px", borderTop:`1px solid ${C.border}`, background:C.s2 }}>
                      {saveState.saved ? (
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:12, color:C.success, fontWeight:600 }}>✓ Saved as "{saveState.savedName}"</span>
                          <button onClick={() => { onClose(); requestAnimationFrame(() => onNavigate("research")); }}
                            style={{ marginLeft:"auto", ...S.btnGhost, fontSize:11, padding:"4px 10px" }}>View in Research →</button>
                        </div>
                      ) : (
                        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                          <input value={saveState.name ?? ""} onChange={e => setKwSave(p => ({ ...p, [m.id]: { ...p[m.id], name: e.target.value } }))}
                            onKeyDown={e => e.key === "Enter" && doSave()} placeholder="List name…"
                            style={{ ...S.input, flex:1, fontSize:12, padding:"6px 10px" }} />
                          <button onClick={doSave} style={{ ...S.btn, fontSize:11, padding:"6px 14px", flexShrink:0 }}>Save</button>
                          <button onClick={() => navigator.clipboard.writeText(kws.map((k,i) => `${i+1}. ${k.keyword} — Vol: ${k.volume||"?"} Diff: ${k.difficulty||"?"} Opp: ${k.opportunity||"?"}`).join("\n"))}
                            style={{ ...S.btnGhost, fontSize:11, padding:"6px 10px", flexShrink:0 }}>Copy</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {/* Action result: topics */}
              {m.actionResult?.kind === "topics" && (() => {
                const topics = m.actionResult.topics || [];
                const ANGLE_COLOR = { Educational:"#6366f1", Promotional:C.accent, Storytelling:"#ec4899", Seasonal:"#10b981" };
                const TYPE_COLOR  = { "Blog Post":"#6366f1", "Email":"#f59e0b", "Social Post":"#ec4899", "Video Script":"#10b981" };
                const ss = topicSave[m.id] || {};
                const savedItems = ss.savedItems || {};
                const openMenu = ss.openMenu ?? null; // null | "all" | number (topic index)

                const upd = (patch) => setTopicSave(p => ({ ...p, [m.id]: { ...p[m.id], ...patch } }));

                // Persist topics to a list — either append to existing or create new
                const persist = async (topicsToSave, listId, newName, onDone) => {
                  try {
                    const all = await store.get("topic-lists") || [];
                    if (listId) {
                      // Append to existing list
                      const stamped = topicsToSave.map(t => ({ ...t, savedAt: new Date().toISOString() }));
                      const updated = all.map(l => l.id === listId ? { ...l, topics: [...(l.topics||[]), ...stamped] } : l);
                      await store.set("topic-lists", updated);
                    } else {
                      // Create new list
                      const name = (newName||"").trim() || `Topics — ${new Date().toLocaleDateString()}`;
                      const entry = { id:`tl_${Date.now()}`, name, createdAt:new Date().toISOString(), topics:topicsToSave.map(t=>({...t, savedAt:new Date().toISOString()})) };
                      await store.set("topic-lists", [entry, ...all]);
                    }
                    lastTopicsRef.current = topics;
                    window.dispatchEvent(new CustomEvent("topic-list-saved"));
                    onDone();
                  } catch(e) { upd({ error:"Save failed — try again" }); }
                };

                // Inline list-picker menu component
                const ListPicker = ({ onPick }) => {
                  const newName = ss.newListName || "";
                  return (
                    <div style={{ background:C.s1, border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden", boxShadow:"0 4px 16px rgba(0,0,0,0.10)", marginTop:4 }}>
                      {savedTopicLists.length > 0 && (
                        <>
                          <div style={{ padding:"6px 10px 4px", fontSize:10, fontWeight:600, color:C.t3, textTransform:"uppercase", letterSpacing:"0.06em" }}>Add to existing list</div>
                          {savedTopicLists.map(l => (
                            <button key={l.id} onClick={() => onPick(l.id, null)}
                              style={{ display:"block", width:"100%", textAlign:"left", padding:"7px 12px", background:"transparent", border:"none", borderBottom:`1px solid ${C.border}20`, color:C.text, fontSize:12, cursor:"pointer", fontFamily:"inherit" }}
                              onMouseOver={e => e.currentTarget.style.background=C.s2}
                              onMouseOut={e => e.currentTarget.style.background="transparent"}>
                              <span style={{ fontWeight:600 }}>{l.name}</span>
                              <span style={{ color:C.t3, marginLeft:6 }}>{l.topics?.length||0} topics</span>
                            </button>
                          ))}
                          <div style={{ padding:"6px 10px 4px", fontSize:10, fontWeight:600, color:C.t3, textTransform:"uppercase", letterSpacing:"0.06em", borderTop:`1px solid ${C.border}` }}>Or create new list</div>
                        </>
                      )}
                      <div style={{ padding:"8px 10px", display:"flex", gap:6 }}>
                        <input
                          autoFocus
                          value={newName}
                          onChange={e => upd({ newListName: e.target.value })}
                          onKeyDown={e => { if (e.key==="Enter" && newName.trim()) onPick(null, newName); if (e.key==="Escape") upd({ openMenu:null }); }}
                          placeholder={savedTopicLists.length ? "New list name…" : "List name…"}
                          style={{ ...S.input, flex:1, fontSize:12, padding:"5px 9px" }}
                        />
                        <button onClick={() => onPick(null, newName || `Topics — ${new Date().toLocaleDateString()}`)}
                          style={{ ...S.btn, fontSize:11, padding:"5px 12px", flexShrink:0 }}>Save</button>
                      </div>
                    </div>
                  );
                };

                return (
                  <div style={{ width:"100%", background:C.s1, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden", boxShadow:S.card.boxShadow }}>
                    <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8, background:C.s2 }}>
                      <span style={{ fontSize:15 }}>💡</span>
                      <div style={{ fontSize:12, fontWeight:600, color:C.text }}>Content Topic Ideas</div>
                      <span style={{ marginLeft:"auto", fontSize:10, color:C.t3 }}>{topics.length} topics</span>
                    </div>
                    <div style={{ maxHeight:360, overflowY:"auto" }}>
                      {topics.map((t, i) => (
                        <div key={i} style={{ borderBottom:`1px solid ${C.border}20` }}>
                          <div style={{ padding:"10px 14px", display:"flex", alignItems:"flex-start", gap:10 }}>
                            <div style={{ fontSize:11, fontWeight:700, color:C.t3, width:16, flexShrink:0, paddingTop:2 }}>{i+1}</div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:3, lineHeight:1.4 }}>{t.title}</div>
                              <div style={{ fontSize:11, color:C.t2, lineHeight:1.5, marginBottom:5 }}>{t.description}</div>
                              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                                <span style={{ fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:8, background:`${TYPE_COLOR[t.type]||"#6366f1"}18`, color:TYPE_COLOR[t.type]||"#6366f1", textTransform:"uppercase", letterSpacing:"0.04em" }}>{t.type}</span>
                                {t.angle && <span style={{ fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:8, background:`${ANGLE_COLOR[t.angle]||C.t3}15`, color:ANGLE_COLOR[t.angle]||C.t3, textTransform:"uppercase" }}>{t.angle}</span>}
                              </div>
                            </div>
                            <div style={{ flexShrink:0, display:"flex", flexDirection:"column", gap:4, alignItems:"flex-end" }}>
                              {savedItems[i] ? (
                                <span style={{ fontSize:9, color:C.success, fontWeight:700 }}>✓ Saved</span>
                              ) : (
                                <button onClick={() => upd({ openMenu: openMenu===i ? null : i, newListName:"" })}
                                  style={{ fontSize:9, padding:"3px 8px", borderRadius:6, border:`1px solid ${openMenu===i?C.accent:C.border}`, background:openMenu===i?`${C.accent}10`:C.s2, color:openMenu===i?C.accent:C.t2, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                                  + Save
                                </button>
                              )}
                              <button onClick={() => { window.dispatchEvent(new CustomEvent("content-topic-selected", { detail: t })); requestAnimationFrame(() => { onNavigate("content"); onClose(); }); }}
                                style={{ fontSize:9, padding:"3px 8px", borderRadius:6, border:`1px solid ${C.accent}40`, background:`${C.accent}10`, color:C.accent, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                                Write →
                              </button>
                            </div>
                          </div>
                          {/* Per-topic list picker */}
                          {openMenu === i && (
                            <div style={{ padding:"0 14px 10px 40px" }}>
                              <ListPicker onPick={(listId, newName) => persist([t], listId, newName, () => {
                                upd({ savedItems: { ...savedItems, [i]: true }, openMenu: null, newListName:"", error:null });
                              })} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {/* Footer: Save All */}
                    <div style={{ padding:"10px 12px", borderTop:`1px solid ${C.border}`, background:C.s2 }}>
                      {ss.savedAll ? (
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:12, color:C.success, fontWeight:600 }}>✓ Saved as "{ss.savedName}"</span>
                          <button onClick={() => { requestAnimationFrame(() => { onNavigate("content"); onClose(); }); }}
                            style={{ marginLeft:"auto", ...S.btnGhost, fontSize:11, padding:"4px 10px" }}>View →</button>
                        </div>
                      ) : (
                        <div>
                          {ss.error && <div style={{ fontSize:11, color:C.danger, marginBottom:6 }}>{ss.error}</div>}
                          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                            <button onClick={() => upd({ openMenu: openMenu==="all" ? null : "all", newListName:"" })}
                              style={{ ...S.btn, fontSize:11, padding:"6px 14px", flexShrink:0, ...(openMenu==="all" ? { background:C.t2 } : {}) }}>
                              {openMenu==="all" ? "Cancel" : "Save All"}
                            </button>
                            <button onClick={() => navigator.clipboard.writeText(topics.map((t,i) => `${i+1}. ${t.title} (${t.type})`).join("\n"))}
                              style={{ ...S.btnGhost, fontSize:11, padding:"6px 10px", flexShrink:0 }}>Copy</button>
                          </div>
                          {/* Save All list picker */}
                          {openMenu === "all" && (
                            <ListPicker onPick={(listId, newName) => persist(topics, listId, newName, () => {
                              const label = listId ? (savedTopicLists.find(l=>l.id===listId)?.name||"list") : (newName||"").trim()||`Topics — ${new Date().toLocaleDateString()}`;
                              upd({ savedAll:true, savedName:label, openMenu:null, newListName:"", error:null });
                            })} />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
              {/* Action result: topics saved */}
              {m.actionResult?.kind === "topics_saved" && (
                <div style={{ ...S.card, padding:"14px 16px", display:"flex", alignItems:"center", gap:12, border:`1px solid ${C.success}40`, background:`${C.success}08` }}>
                  <span style={{ fontSize:18, color:C.success }}>✓</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>"{m.actionResult.name}" saved</div>
                    <div style={{ fontSize:11, color:C.t2, marginTop:2 }}>{m.actionResult.count} topics saved to your list</div>
                  </div>
                </div>
              )}
              {/* Action result: keywords saved */}
              {m.actionResult?.kind === "keywords_saved" && (
                <div style={{ ...S.card, padding:"14px 16px", display:"flex", alignItems:"center", gap:12, border:`1px solid ${C.success}40`, background:`${C.success}08` }}>
                  <span style={{ fontSize:18, color:C.success }}>✓</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>"{m.actionResult.name}" saved</div>
                    <div style={{ fontSize:11, color:C.t2, marginTop:2 }}>{m.actionResult.count} keywords · Find it in Research → My Lists</div>
                  </div>
                  <button onClick={() => { onClose(); requestAnimationFrame(() => onNavigate("research")); }}
                    style={{ ...S.btnGhost, fontSize:11, padding:"5px 10px", flexShrink:0 }}>View →</button>
                </div>
              )}
              {/* Action result: social */}
              {m.actionResult?.kind === "social" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:`${C.accent}18`, color:C.accent, fontWeight:700, textTransform:"capitalize" }}>{m.actionResult.platform}</span>
                    <span style={{ fontSize:11, color:C.t2 }}>Social Post</span>
                  </div>
                  <div style={{ padding:"10px 12px", fontSize:12, color:C.text, lineHeight:1.7, maxHeight:160, overflowY:"auto", whiteSpace:"pre-wrap" }}>
                    {m.actionResult.text}
                  </div>
                  <div style={{ padding:"8px 12px", borderTop:`1px solid ${C.border}`, display:"flex", gap:6 }}>
                    <button onClick={() => navigator.clipboard.writeText(m.actionResult.text)}
                      style={{ ...S.btn, fontSize:11, padding:"5px 12px" }}>Copy Post</button>
                    <button onClick={() => { onNavigate("social"); onClose(); }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 10px" }}>Open Social →</button>
                  </div>
                </div>
              )}
              {/* Action result: campaign draft (single channel) */}
              {m.actionResult?.kind === "campaign" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:10, background:`${GM}18`, color:GM, fontWeight:700, textTransform:"uppercase" }}>{m.actionResult.campaignType}</span>
                    <span style={{ fontSize:11, color:C.t2, flex:1 }}>{m.actionResult.subject || m.actionResult.goal}</span>
                  </div>
                  <div style={{ padding:"10px 12px", fontSize:12, color:C.text, lineHeight:1.7, maxHeight:160, overflowY:"auto", whiteSpace:"pre-wrap" }}>
                    {m.actionResult.text}
                  </div>
                  <div style={{ padding:"8px 12px", borderTop:`1px solid ${C.border}`, display:"flex", gap:6 }}>
                    <button onClick={() => navigator.clipboard.writeText(m.actionResult.text)}
                      style={{ ...S.btn, fontSize:11, padding:"5px 12px" }}>Copy</button>
                    <button onClick={() => { onClose(); requestAnimationFrame(() => { onNavigate("campaigns"); window.dispatchEvent(new CustomEvent("campaigns-show-tab", { detail: "mine" })); }); }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 10px" }}>Open Campaigns →</button>
                  </div>
                </div>
              )}
              {/* Action result: campaign generating (live progress) */}
              {m.actionResult?.kind === "campaign_generating" && (() => {
                const { templateName, channels, completedChannels, currentChannel, total } = m.actionResult;
                const pct = total > 0 ? Math.round(completedChannels.length / total * 100) : 0;
                return (
                  <div style={{ width:"100%", background:C.s3, border:`1px solid ${CM_COL}40`, borderRadius:10, overflow:"hidden" }}>
                    <div style={{ padding:"10px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                      <div style={{ width:14, height:14, border:`2px solid ${CM_COL}`, borderTopColor:"transparent", borderRadius:"50%", animation:"spin 0.8s linear infinite", flexShrink:0 }} />
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{templateName} Campaign</div>
                        <div style={{ fontSize:10, color:C.t2, marginTop:1 }}>Writing {currentChannel}…</div>
                      </div>
                      <span style={{ fontSize:11, color:CM_COL, fontWeight:700 }}>{pct}%</span>
                      <button onClick={() => { abortCampaignRef.current = true; }}
                        style={{ fontSize:10, padding:"3px 9px", borderRadius:6, border:`1px solid ${C.danger}50`, background:`${C.danger}10`, color:C.danger, cursor:"pointer", fontFamily:"inherit", fontWeight:600, flexShrink:0 }}>
                        ◼ Stop
                      </button>
                    </div>
                    <div style={{ padding:"8px 12px 4px" }}>
                      <div style={{ height:3, background:C.s2, borderRadius:2, overflow:"hidden", marginBottom:10 }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:`linear-gradient(90deg,${CM_COL},#a78bfa)`, borderRadius:2, transition:"width 0.4s ease" }} />
                      </div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:4, paddingBottom:8 }}>
                        {(channels || []).map(ch => {
                          const done = completedChannels.includes(ch);
                          const running = !done && ch === (ACTIONS.find(a => a.label === currentChannel)?.id);
                          return (
                            <span key={ch} style={{ fontSize:10, padding:"2px 7px", borderRadius:10, fontWeight:600, textTransform:"capitalize",
                              background: done ? `${CM_COL}25` : running ? `${CM_COL}12` : C.s2,
                              color: done ? CM_COL : running ? C.t2 : C.t3,
                              border: `1px solid ${done ? CM_COL+"60" : C.border}`,
                            }}>
                              {done ? "✓ " : running ? "◌ " : ""}{ch.replace(/_/g," ")}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}
              {/* Action result: campaign created (multi-channel, content ready) */}
              {m.actionResult?.kind === "campaign_created" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${CM_COL}40`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:16, color:CM_COL }}>✦</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{m.actionResult.campaign.name}</div>
                      <div style={{ fontSize:10, color:m.actionResult.stopped ? C.t2 : C.success, marginTop:1 }}>
                        {m.actionResult.stopped ? `◼ Stopped — ${m.actionResult.channelCount} channels saved` : `✓ ${m.actionResult.channelCount} channels generated & ready to publish`}
                      </div>
                    </div>
                  </div>
                  <div style={{ padding:"10px 12px", display:"flex", flexWrap:"wrap", gap:4 }}>
                    {(m.actionResult.channels || []).map(ch => (
                      <span key={ch} style={{ fontSize:10, padding:"2px 7px", borderRadius:10, background:`${CM_COL}20`, color:CM_COL, fontWeight:600, textTransform:"capitalize", border:`1px solid ${CM_COL}50` }}>
                        ✓ {ch.replace(/_/g," ")}
                      </span>
                    ))}
                  </div>
                  <div style={{ padding:"8px 12px", borderTop:`1px solid ${C.border}` }}>
                    <button onClick={() => {
                        onClose();
                        requestAnimationFrame(() => {
                          onNavigate("campaigns");
                          window.dispatchEvent(new CustomEvent("campaigns-show-tab", { detail: "mine" }));
                        });
                      }}
                      style={{ ...S.btn, fontSize:11, padding:"5px 12px", background:CM_COL, width:"100%", justifyContent:"center" }}>
                      Open Campaigns →
                    </button>
                  </div>
                </div>
              )}

              {/* ── Review reply cards ──────────────────────────────── */}
              {m.actionResult?.kind === "review_reply" && (() => {
                if (m.actionResult.allResponded) return (
                  <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", fontSize:13, color:C.success, fontWeight:600 }}>
                    ✓ All caught up — no unresponded reviews.
                  </div>
                );
                return (
                  <div style={{ width:"100%", display:"flex", flexDirection:"column", gap:8 }}>
                    {(m.actionResult.reviews || []).map((rv, ri) => (
                      <ReviewReplyCard key={rv.id || ri} review={rv} biz={biz} cat={cat} loc={loc} profile={profile} onNavigate={onNavigate} onClose={onClose} />
                    ))}
                  </div>
                );
              })()}

              {/* ── Review summary card ─────────────────────────────── */}
              {m.actionResult?.kind === "review_summary" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${RV}40`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:18, color:RV }}>★</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Review Analysis</div>
                      <div style={{ fontSize:11, color:C.t3 }}>{m.actionResult.total} reviews · avg {m.actionResult.avg}★ · {m.actionResult.unresponded} unresponded</div>
                    </div>
                  </div>
                  <div style={{ padding:"10px 14px", display:"flex", gap:6, marginBottom:4 }}>
                    {(m.actionResult.byRating || []).map(b => (
                      <div key={b.stars} style={{ flex:1, textAlign:"center" }}>
                        <div style={{ fontSize:11, color:RV, fontWeight:700 }}>{"★".repeat(b.stars)}</div>
                        <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{b.count}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding:"0 14px 12px", fontSize:12, color:C.t2, lineHeight:1.7 }}>{renderMarkdown(m.actionResult.summary)}</div>
                  {m.actionResult.unresponded > 0 && (
                    <div style={{ padding:"8px 14px", borderTop:`1px solid ${C.border}` }}>
                      <button onClick={() => { onClose(); onNavigate("reviews"); }}
                        style={{ ...S.btn, background:RV, fontSize:11, padding:"5px 12px", width:"100%", justifyContent:"center" }}>
                        Respond to {m.actionResult.unresponded} unresponded →
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ── Review request card ─────────────────────────────── */}
              {m.actionResult?.kind === "review_request" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${RV}40`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:14, color:RV }}>◎</span>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Review Request · <span style={{ textTransform:"capitalize", fontWeight:400, color:C.t2 }}>{m.actionResult.channel}</span></div>
                  </div>
                  <div style={{ padding:"12px 14px", fontSize:12, color:C.text, lineHeight:1.7 }}>{renderMarkdown(m.actionResult.message)}</div>
                  <div style={{ padding:"8px 14px", borderTop:`1px solid ${C.border}`, display:"flex", gap:8 }}>
                    <button onClick={() => navigator.clipboard.writeText(m.actionResult.message)}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px", flex:1 }}>Copy</button>
                    <button onClick={() => send(`Generate a different ${m.actionResult.channel} review request, different tone`)}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px", flex:1 }}>Regenerate</button>
                  </div>
                </div>
              )}

              {/* ── CRM: contact added ─────────────────────────────── */}
              {m.actionResult?.kind === "crm_contact_added" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.success}40`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:14, color:C.success }}>◈</span>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Contact Added</div>
                  </div>
                  <div style={{ padding:"10px 14px" }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>{m.actionResult.contact.name}</div>
                    {m.actionResult.contact.email && <div style={{ fontSize:12, color:C.t2, marginBottom:2 }}>✉ {m.actionResult.contact.email}</div>}
                    {m.actionResult.contact.phone && <div style={{ fontSize:12, color:C.t2, marginBottom:2 }}>✆ {m.actionResult.contact.phone}</div>}
                    <span style={{ fontSize:10, padding:"2px 8px", borderRadius:8, background:`${C.accent}15`, color:C.accent, fontWeight:700, textTransform:"capitalize" }}>{m.actionResult.contact.type}</span>
                  </div>
                  <div style={{ padding:"8px 14px", borderTop:`1px solid ${C.border}` }}>
                    <button onClick={() => { onNavigate("crm"); onClose(); }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px" }}>View in CRM →</button>
                  </div>
                </div>
              )}

              {/* ── CRM: contact found ─────────────────────────────── */}
              {m.actionResult?.kind === "crm_contact_found" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:14, color:C.accent }}>◈</span>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>
                      {m.actionResult.contacts?.length ? `${m.actionResult.contacts.length} Contact${m.actionResult.contacts.length > 1 ? "s" : ""} Found` : "No Contacts Found"}
                    </div>
                    {m.actionResult.query && <span style={{ fontSize:11, color:C.t3, marginLeft:"auto" }}>"{m.actionResult.query}"</span>}
                  </div>
                  {m.actionResult.contacts?.length > 0 ? (
                    <div>
                      {m.actionResult.contacts.map((c, ci) => (
                        <div key={c.id||ci} style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}20` }}>
                          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:3 }}>{c.name}</div>
                          <div style={{ display:"flex", gap:10, flexWrap:"wrap", marginBottom:3 }}>
                            {c.email && <span style={{ fontSize:11, color:C.t2 }}>✉ {c.email}</span>}
                            {c.phone && <span style={{ fontSize:11, color:C.t2 }}>✆ {c.phone}</span>}
                          </div>
                          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                            <span style={{ fontSize:10, padding:"2px 7px", borderRadius:8, background:`${C.accent}12`, color:C.accent, fontWeight:700, textTransform:"capitalize" }}>{c.type}</span>
                            {c.notes && <span style={{ fontSize:11, color:C.t3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{c.notes.slice(0,60)}{c.notes.length>60?"…":""}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ padding:"16px 14px", fontSize:12, color:C.t3, textAlign:"center" }}>No contacts matched "{m.actionResult.query}".</div>
                  )}
                  <div style={{ padding:"8px 14px", borderTop:`1px solid ${C.border}` }}>
                    <button onClick={() => { onNavigate("crm"); onClose(); }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px" }}>View in CRM →</button>
                  </div>
                </div>
              )}

              {/* ── CRM: note added ─────────────────────────────── */}
              {m.actionResult?.kind === "crm_note_added" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.success}40`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:14, color:C.success }}>✓</span>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Note Added to {m.actionResult.contact?.name}</div>
                  </div>
                  <div style={{ padding:"10px 14px", fontSize:12, color:C.t2, lineHeight:1.6, fontStyle:"italic" }}>"{m.actionResult.note}"</div>
                  <div style={{ padding:"8px 14px", borderTop:`1px solid ${C.border}` }}>
                    <button onClick={() => { onNavigate("crm"); onClose(); }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px" }}>View in CRM →</button>
                  </div>
                </div>
              )}

              {/* ── Campaign overview card ──────────────────────────── */}
              {m.actionResult?.kind === "campaign_overview" && (() => {
                const STATUS_COL = { active:C.success, draft:C.t3, paused:"#f59e0b" };
                return (
                  <div style={{ width:"100%", background:C.s3, border:`1px solid ${CM_COL}40`, borderRadius:10, overflow:"hidden" }}>
                    <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:14, color:CM_COL }}>⚡</span>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Campaign Overview</div>
                      <div style={{ marginLeft:"auto", display:"flex", gap:10 }}>
                        {[["Active",m.actionResult.active,C.success],["Draft",m.actionResult.draft,C.t3],["Paused",m.actionResult.paused,"#f59e0b"]].map(([l,v,c])=>(
                          <div key={l} style={{ textAlign:"center" }}>
                            <div style={{ fontSize:16, fontWeight:700, color:c }}>{v}</div>
                            <div style={{ fontSize:9, color:C.t3, textTransform:"uppercase" }}>{l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {m.actionResult.campaigns?.length > 0 ? (
                      <div style={{ maxHeight:200, overflowY:"auto" }}>
                        {m.actionResult.campaigns.map((c, ci) => (
                          <div key={c.id||ci} style={{ padding:"9px 14px", borderBottom:`1px solid ${C.border}20`, display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div style={{ fontSize:12, fontWeight:600, color:C.text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.name}</div>
                              {c.goal && <div style={{ fontSize:11, color:C.t3 }}>{c.goal}</div>}
                            </div>
                            <span style={{ fontSize:10, padding:"2px 8px", borderRadius:8, fontWeight:700, textTransform:"capitalize", background:`${STATUS_COL[c.status]||C.t3}18`, color:STATUS_COL[c.status]||C.t3 }}>{c.status||"active"}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ padding:"16px 14px", fontSize:12, color:C.t3, textAlign:"center" }}>No campaigns yet.</div>
                    )}
                    <div style={{ padding:"8px 14px", borderTop:`1px solid ${C.border}` }}>
                      <button onClick={() => { onNavigate("campaigns"); onClose(); }}
                        style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px" }}>Open Campaigns →</button>
                    </div>
                  </div>
                );
              })()}

              {/* ── Campaign updated (paused/resumed) ──────────────── */}
              {m.actionResult?.kind === "campaign_updated" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${m.actionResult.action==="paused"?"#f59e0b":C.success}40`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"12px 14px", display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:16, color: m.actionResult.action==="paused" ? "#f59e0b" : C.success }}>
                      {m.actionResult.action === "paused" ? "⏸" : "▶"}
                    </span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text }}>
                        {m.actionResult.action === "paused" ? "Campaign Paused" : "Campaign Resumed"}
                      </div>
                      <div style={{ fontSize:12, color:C.t2, marginTop:2 }}>{m.actionResult.campaign?.name}</div>
                    </div>
                  </div>
                  <div style={{ padding:"0 14px 10px 14px" }}>
                    <button onClick={() => { onNavigate("campaigns"); onClose(); }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px" }}>Open Campaigns →</button>
                  </div>
                </div>
              )}

              {m.actionResult?.kind === "reservation_added" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.accent}40`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ color:C.accent, fontSize:16 }}>◉</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Booking Confirmed</div>
                      <div style={{ fontSize:11, color:C.success }}>✓ Saved to your bookings</div>
                    </div>
                  </div>
                  <div style={{ padding:"12px 14px", display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:8 }}>
                    {[
                      ["Guest", m.actionResult.booking.name],
                      ["Date", m.actionResult.booking.date],
                      ["Time", m.actionResult.booking.time],
                      ["Party", `${m.actionResult.booking.party} guests`],
                    ].map(([label, val]) => (
                      <div key={label}>
                        <div style={{ fontSize:10, color:C.t3, textTransform:"uppercase", letterSpacing:"0.05em" }}>{label}</div>
                        <div style={{ fontSize:13, fontWeight:600, color:C.text, marginTop:2 }}>{val}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding:"0 14px 12px" }}>
                    <button onClick={() => { onClose(); onNavigate("reservations"); }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px", width:"100%" }}>
                      View in Bookings →
                    </button>
                  </div>
                </div>
              )}

              {m.actionResult?.kind === "reservation_overview" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.border}`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", gap:16 }}>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:20, fontWeight:700, color:C.accent }}>{m.actionResult.todayList.length}</div>
                      <div style={{ fontSize:10, color:C.t3 }}>Today</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:20, fontWeight:700, color:C.text }}>{m.actionResult.upcoming.length}</div>
                      <div style={{ fontSize:10, color:C.t3 }}>Upcoming</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:20, fontWeight:700, color:C.t2 }}>{m.actionResult.total}</div>
                      <div style={{ fontSize:10, color:C.t3 }}>Total</div>
                    </div>
                  </div>
                  {[...m.actionResult.todayList, ...m.actionResult.upcoming].slice(0, 6).map((r, i) => (
                    <div key={r.id || i} style={{ padding:"8px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", gap:10, alignItems:"center" }}>
                      <div style={{ width:36, height:36, borderRadius:8, background:`${C.accent}15`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:C.accent, flexShrink:0, textAlign:"center", lineHeight:1.2 }}>
                        {r.date?.slice(5)?.replace("-","/")}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{r.name}</div>
                        <div style={{ fontSize:11, color:C.t2 }}>{r.time} · Party of {r.party}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ padding:"8px 14px" }}>
                    <button onClick={() => { onClose(); onNavigate("reservations"); }}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px", width:"100%" }}>
                      Open Bookings →
                    </button>
                  </div>
                </div>
              )}

              {m.actionResult?.kind === "reservation_cancelled" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 14px", display:"flex", gap:10, alignItems:"center" }}>
                  <span style={{ fontSize:18, color:C.t3 }}>✕</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Booking Cancelled</div>
                    <div style={{ fontSize:11, color:C.t2 }}>{m.actionResult.booking.name} · {m.actionResult.booking.date} at {m.actionResult.booking.time}</div>
                  </div>
                </div>
              )}

              {m.actionResult?.kind === "gbp_post" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid #4285f440`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:14, fontWeight:900, color:"#4285f4" }}>G</span>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Google Business Post</div>
                    <span style={{ fontSize:10, color:C.t3, marginLeft:"auto" }}>{m.actionResult.text.length} chars</span>
                  </div>
                  <div style={{ padding:"12px 14px", fontSize:12, color:C.text, lineHeight:1.7 }}>{renderMarkdown(m.actionResult.text)}</div>
                  <div style={{ padding:"0 14px 12px", display:"flex", gap:8 }}>
                    <button onClick={() => navigator.clipboard.writeText(m.actionResult.text)}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px", flex:1 }}>Copy</button>
                    <button onClick={() => { onClose(); onNavigate("gbp"); }}
                      style={{ ...S.btn, background:"#4285f4", fontSize:11, padding:"5px 12px", flex:1 }}>
                      Open GBP →
                    </button>
                  </div>
                </div>
              )}

              {m.actionResult?.kind === "gbp_qa_pairs" && (
                <div style={{ width:"100%", background:C.s3, border:`1px solid #4285f440`, borderRadius:10, overflow:"hidden" }}>
                  <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:14, fontWeight:900, color:"#4285f4" }}>G</span>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>GBP Q&A — {m.actionResult.pairs.length} pairs</div>
                  </div>
                  <div style={{ maxHeight:300, overflowY:"auto" }}>
                    {(m.actionResult.pairs || []).map((p, i) => (
                      <div key={i} style={{ padding:"10px 14px", borderBottom: i < m.actionResult.pairs.length-1 ? `1px solid ${C.border}` : "none" }}>
                        <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:3 }}>Q: {p.q}</div>
                        <div style={{ fontSize:12, color:C.t2, lineHeight:1.5 }}>A: {p.a}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding:"8px 14px", borderTop:`1px solid ${C.border}` }}>
                    <button onClick={() => navigator.clipboard.writeText((m.actionResult.pairs||[]).map(p=>`Q: ${p.q}\nA: ${p.a}`).join("\n\n"))}
                      style={{ ...S.btnGhost, fontSize:11, padding:"5px 12px", width:"100%" }}>Copy All</button>
                  </div>
                </div>
              )}

              {/* ── Dashboard briefing quick-reply chips ────────────── */}
              {m.isDashboardBrief && (
                <div style={{ display:"flex", flexDirection:"column", gap:6, width:"100%", marginTop:4 }}>
                  {(ctx?.unresponded > 0) && (
                    <button onClick={() => send("Respond to my unresponded reviews")}
                      style={{ padding:"8px 12px", background:C.s1, border:`1px solid ${C.border}`, borderRadius:6, color:C.t2, fontSize:12, fontFamily:"inherit", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:8, transition:"all 0.15s" }}
                      onMouseOver={e => { e.currentTarget.style.background=`${RV}10`; e.currentTarget.style.borderColor=`${RV}60`; e.currentTarget.style.color=RV; }}
                      onMouseOut={e  => { e.currentTarget.style.background=C.s1; e.currentTarget.style.borderColor=C.border; e.currentTarget.style.color=C.t2; }}>
                      <span style={{ fontSize:13 }}>★</span> Respond to {ctx.unresponded} unresponded review{ctx.unresponded > 1 ? "s" : ""}
                    </button>
                  )}
                  <button onClick={() => send("Show campaign status")}
                    style={{ padding:"8px 12px", background:C.s1, border:`1px solid ${C.border}`, borderRadius:6, color:C.t2, fontSize:12, fontFamily:"inherit", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:8, transition:"all 0.15s" }}
                    onMouseOver={e => { e.currentTarget.style.background=`${CM_COL}10`; e.currentTarget.style.borderColor=`${CM_COL}60`; e.currentTarget.style.color=CM_COL; }}
                    onMouseOut={e  => { e.currentTarget.style.background=C.s1; e.currentTarget.style.borderColor=C.border; e.currentTarget.style.color=C.t2; }}>
                    <span style={{ fontSize:13 }}>⚡</span> Show campaign status
                  </button>
                  <button onClick={() => send("Add a new contact")}
                    style={{ padding:"8px 12px", background:C.s1, border:`1px solid ${C.border}`, borderRadius:6, color:C.t2, fontSize:12, fontFamily:"inherit", cursor:"pointer", textAlign:"left", display:"flex", alignItems:"center", gap:8, transition:"all 0.15s" }}
                    onMouseOver={e => { e.currentTarget.style.background=`${C.accent}10`; e.currentTarget.style.borderColor=`${C.accent}60`; e.currentTarget.style.color=C.accent; }}
                    onMouseOut={e  => { e.currentTarget.style.background=C.s1; e.currentTarget.style.borderColor=C.border; e.currentTarget.style.color=C.t2; }}>
                    <span style={{ fontSize:13 }}>◈</span> Add a contact
                  </button>
                </div>
              )}
            </div>
          ))
        )}
        {loading && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-start", gap:5 }}>
            <div style={{ fontSize:10, color:C.t3, marginLeft:4, display:"flex", alignItems:"center", gap:4 }}>
              <span style={{ color:C.accent }}>✦</span> {wlConfig?.app_name||"Kurb AI"}
            </div>
            <div style={{ padding:"9px 14px", background:C.s2, borderRadius:"3px 14px 14px 14px", fontSize:12, color:C.t3, display:"flex", gap:4, alignItems:"center" }}>
              <span style={{ display:"inline-block", animation:"pulse 1.2s infinite" }}>●</span>
              <span style={{ display:"inline-block", animation:"pulse 1.2s infinite 0.2s" }}>●</span>
              <span style={{ display:"inline-block", animation:"pulse 1.2s infinite 0.4s" }}>●</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Context chip */}
      {messages.length > 0 && !showHistory && !viewingConv && (
        <div style={{ padding:"6px 12px", borderTop:`1px solid ${C.border}20`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:10, color:C.t3 }}>📍 {NAV_LABELS[mod] || mod}</span>
          <button onClick={startNewChat} style={{ fontSize:10, color:C.t4, background:"none", border:"none", cursor:"pointer", fontFamily:"inherit" }}>New Chat</button>
        </div>
      )}

      {/* Input — hidden when browsing history */}
      <div style={{ padding:"10px 12px", borderTop:`1px solid ${C.border}`, flexShrink:0, display: (showHistory || viewingConv) ? "none" : "block" }}>
        <div style={{ display:"flex", gap:8, alignItems:"flex-end", background:C.s2, border:`1px solid ${C.border}`, borderRadius:10, padding:"6px 8px 6px 12px" }}>
          <textarea ref={inputRef}
            value={input}
            onChange={e => { setInput(e.target.value); e.target.style.height="auto"; e.target.style.height=Math.min(e.target.scrollHeight,120)+"px"; }}
            onKeyDown={e => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask anything… (Enter to send)"
            rows={1}
            style={{ flex:1, background:"transparent", border:"none", color:C.text, fontSize:12, fontFamily:"inherit", outline:"none", resize:"none", lineHeight:1.5, maxHeight:120, overflowY:"auto" }}
          />
          <button onClick={() => send()} disabled={loading || !input.trim()}
            style={{ width:32, height:32, borderRadius:5, background: (loading || !input.trim()) ? C.s3 : "linear-gradient(135deg, #7A5AF8 0%, #6B21A8 100%)", border:"none", cursor:(loading || !input.trim())?"not-allowed":"pointer", color:"#fff", fontSize:15, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all 0.12s", fontWeight:700 }}>
            ↑
          </button>
        </div>
        <div style={{ fontSize:10, color:C.t4, marginTop:5, textAlign:"center" }}>Shift+Enter for new line</div>
      </div>
    </div>
  );
}

export default AIAssistant;
export { AI_MODULE_REGISTRY, buildAppContext, ReviewReplyCard };
